/**
 * Plain-English admin-config registry (`pending-plain-english-admin-config`).
 *
 * Every entry maps a dot-path into `AdminConfigSchema` (e.g.
 * `email.smtpHost`) to plain-English metadata an Operator persona can
 * search against — labels, help, keywords, affected screens, related
 * settings.
 *
 * v1 scope
 * --------
 * The registry is **hand-authored** and starts at ~50 entries spanning
 * every major tab. The lint script `scripts/check-admin-registry.ts`
 * fails CI when a registry entry references a non-existent schema
 * path; it does NOT yet fail when AdminConfigSchema has a leaf the
 * registry doesn't cover (so adding a new field doesn't block CI for
 * unrelated reasons). v2 inverts this: every leaf must have a row.
 *
 * Scenarios are a second hand-authored list — ~25 short "what do you
 * want to do?" rows that map a user goal to a tab + a set of fields
 * to highlight, with an optional 2–3 step walkthrough.
 */

import { z } from 'zod';
import { AdminConfigSchema } from './admin-config';
import { walkSchemaLeaves } from './admin-config-walker';

// ---------------------------------------------------------------------------
// Field metadata.
// ---------------------------------------------------------------------------

/** Top-level tab id the Admin Settings UI surfaces. Mirrors the tab keys
 *  in `packages/admin-settings/src/App.tsx`. Strings are deliberately
 *  loose so the registry stays a stand-alone authoring surface. */
export const ADMIN_TAB_IDS = [
  'connection',
  'server',
  'branding',
  'email',
  'auth',
  'ai',
  'billing',
  'payments',
  'observability',
  'runner',
  'integrations',
  'theme',
  'reporting',
  'security',
  'editor',
  'onboarding',
  'storage',
  'cdn',
  'secretsManager',
  'cloudBrowser',
  'access',
  'ops',
  'notifications',
  'noCode',
  'approval',
  'accessibility',
  'mockServer',
  'jira',
  /** `pending-tag-search-saved-views-v2` — admin tag-manager UI. */
  'tags',
  /** `pending-synthetic-monitoring` v1.1 — monitors hook + channels + badges. */
  'monitors',
] as const;
export type AdminTabId = (typeof ADMIN_TAB_IDS)[number];

export interface AdminConfigFieldMeta {
  /** Dot-path into AdminConfigSchema. Lowercase, camelCase segments,
   *  e.g. `email.smtpHost`, `mockServer.publicShareLinksEnabled`. */
  path: string;
  /** Tab id (matches AdminTabId). */
  category: AdminTabId;
  /** Operator-facing label. Plain English, ≤80 chars. */
  englishLabel: string;
  /** 1–2 sentence outcome-focused description. */
  englishHelp: string;
  /** Free-text fuzzy-match tokens. Lowercase, single words / short
   *  phrases. Include synonyms ("slack" + "chat" + "webhook"). */
  keywords: string[];
  /** Other field paths frequently configured together. UI shows these
   *  as a "Related" hint in the help side panel. */
  relatedSettings?: string[];
  /** Optional curated affected-screen names (text-only for v1). */
  affectedScreens?: string[];
}

// ---------------------------------------------------------------------------
// Scenarios — curated "what do you want to do?" rows.
// ---------------------------------------------------------------------------

export interface PlainEnglishScenario {
  /** Stable id; English-derived slug. */
  id: string;
  /** The user-phrased question. */
  englishQuestion: string;
  /** Tab the user lands on. */
  targetTab: AdminTabId;
  /** Field paths to highlight on landing. */
  highlightFields: string[];
  /** Optional 2-3 step inline tour. `selector` is a CSS selector the
   *  UI can target (data-field="…" on each field input); `label` is
   *  the popover copy. */
  walkthroughSteps?: { selector: string; label: string }[];
}

// ---------------------------------------------------------------------------
// Auto-derive helpers (v2).
// ---------------------------------------------------------------------------

/**
 * Best-effort guess at the right `AdminTabId` for an auto-derived leaf
 * based on its top-level segment. Hand-authored rows win; this only
 * fires for unauthored leaves so a wrong guess is safer than refusing
 * to emit a row at all.
 *
 * Keep in sync with the actual `AdminConfigSchema` top-level keys.
 * Missing entries fall through to `ops` (safe default since the ops
 * tab already hosts a "raw JSON" editor).
 */
const TOP_SEGMENT_TO_CATEGORY: Record<string, AdminTabId> = {
  connection: 'connection',
  server: 'server',
  branding: 'branding',
  email: 'email',
  auth: 'auth',
  ai: 'ai',
  billing: 'billing',
  payments: 'payments',
  observability: 'observability',
  runner: 'runner',
  integrations: 'integrations',
  theme: 'theme',
  reporting: 'reporting',
  security: 'security',
  editor: 'editor',
  onboarding: 'onboarding',
  storage: 'storage',
  cdn: 'cdn',
  secretsManager: 'secretsManager',
  cloudBrowser: 'cloudBrowser',
  access: 'access',
  ops: 'ops',
  notifications: 'notifications',
  noCode: 'noCode',
  approval: 'approval',
  accessibility: 'accessibility',
  mockServer: 'mockServer',
  jira: 'jira',
  tags: 'tags',
  monitors: 'monitors',
};

/** Pure: lowercase + camel-case-split a segment into keyword tokens. */
export function tokensFromSegment(segment: string): string[] {
  const lower = segment
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .toLowerCase();
  return lower.split(/\s+/).filter((t) => t.length > 1);
}

/** Pure: humanise a camelCase / dot-path leaf into a sentence-case label. */
export function deriveLabelFromPath(path: string): string {
  const last = path.split('.').slice(-1)[0] ?? path;
  const spaced = last.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export interface AutoDeriveOptions {
  /** If true, leaves whose path is already in the hand-authored
   *  registry are skipped (the default — used by the v2 backfill). */
  skipAuthored?: boolean;
  /** Override the category fallback. Defaults to `'ops'`. */
  fallbackCategory?: AdminTabId;
}

interface AutoDeriveInputLeaf {
  path: string;
}

/**
 * Marker keyword every auto-derived row carries. Consumers can filter
 * on this token to (a) badge the row "Auto-derived" in the help-side
 * panel, (b) deprioritise it in fuzzy search vs hand-authored rows.
 */
export const AUTO_DERIVED_KEYWORD = '_autoderived';

/**
 * Pure: derive a minimal `AdminConfigFieldMeta` row for a leaf path.
 *
 * The output passes `AdminConfigFieldMetaSchema` validation by
 * construction. Callers usually then merge it with the hand-authored
 * registry via `mergeRegistry`, but the row is usable stand-alone.
 *
 * The `englishHelp` is a placeholder — the v2 coverage gate accepts
 * the auto-derived row but operators get a "Help text auto-derived —
 * consider hand-authoring." nudge in the help-side-panel UI.
 */
export function deriveMetaForPath(path: string, opts: AutoDeriveOptions = {}): AdminConfigFieldMeta {
  const segments = path.split('.');
  const top = segments[0] ?? '';
  const category = TOP_SEGMENT_TO_CATEGORY[top] ?? opts.fallbackCategory ?? 'ops';
  const englishLabel = deriveLabelFromPath(path);
  // Help text falls back to a sentence built from the leaf's path —
  // not great copy, but at least it's actionable + grounded in the
  // schema so an operator can find the field via search.
  const englishHelp = `Operator-tunable setting at \`${path}\`. Auto-derived label — consider hand-authoring help text in admin-config-meta.ts.`;
  const keywords = Array.from(new Set([AUTO_DERIVED_KEYWORD, ...segments.flatMap(tokensFromSegment)]));
  if (keywords.length === 1) keywords.push(top || 'config');
  return { path, category, englishLabel, englishHelp, keywords };
}

/** Pure: identify whether a row was emitted by `deriveMetaForPath`. */
export function isAutoDerivedMeta(meta: AdminConfigFieldMeta): boolean {
  return meta.keywords.includes(AUTO_DERIVED_KEYWORD);
}

/**
 * Build a combined registry: hand-authored rows first (operator copy
 * wins), then auto-derived rows for every leaf that didn't have one.
 *
 * `leaves` is usually the output of `walkSchemaLeaves(AdminConfigSchema)`
 * from `admin-config-walker.ts` — we import it lazily here to avoid a
 * hard cycle.
 */
export function mergeRegistry(
  authored: AdminConfigFieldMeta[],
  leaves: AutoDeriveInputLeaf[],
  opts: AutoDeriveOptions = {},
): AdminConfigFieldMeta[] {
  const authoredPaths = new Set(authored.map((m) => m.path));
  const merged: AdminConfigFieldMeta[] = [...authored];
  for (const leaf of leaves) {
    if (authoredPaths.has(leaf.path)) continue;
    if (opts.skipAuthored === false) continue;
    merged.push(deriveMetaForPath(leaf.path, opts));
  }
  // Stable order: hand-authored first (preserves the v1 sidebar
  // grouping), then auto-derived sorted alphabetically.
  const head = merged.slice(0, authored.length);
  const tail = merged.slice(authored.length).sort((a, b) => a.path.localeCompare(b.path));
  return [...head, ...tail];
}

// ---------------------------------------------------------------------------
// Zod schemas — used in tests + by the lint script.
// ---------------------------------------------------------------------------

export const AdminTabIdSchema = z.enum(ADMIN_TAB_IDS);

export const AdminConfigFieldMetaSchema = z.object({
  // Dot-separated. Each segment is camelCase by convention but kebab
  // slugs are allowed too because some app-id segments in
  // `AdminConfigSchema` are kebab-cased (`landing-page`,
  // `admin-console`, …). Keep the leading character a lowercase
  // letter so paths can't start with a digit or sigil.
  path: z
    .string()
    .regex(
      /^[a-z][a-zA-Z0-9-]*(\.[a-z][a-zA-Z0-9-]*)*$/,
      'path must be a dot-separated string; each segment starts with a lowercase letter and may contain letters, digits, or hyphens',
    ),
  category: AdminTabIdSchema,
  englishLabel: z.string().min(2).max(120),
  englishHelp: z.string().min(8).max(400),
  keywords: z.array(z.string().min(1)).min(1),
  relatedSettings: z.array(z.string()).optional(),
  affectedScreens: z.array(z.string()).optional(),
});

export const PlainEnglishScenarioSchema = z.object({
  id: z.string().regex(/^[a-z][a-z0-9-]*$/),
  englishQuestion: z.string().min(8).max(160),
  targetTab: AdminTabIdSchema,
  highlightFields: z.array(z.string()).min(1),
  walkthroughSteps: z
    .array(z.object({ selector: z.string().min(1), label: z.string().min(2).max(200) }))
    .max(5)
    .optional(),
});

// ---------------------------------------------------------------------------
// Hand-authored registry.
// ---------------------------------------------------------------------------

export const ADMIN_CONFIG_REGISTRY: AdminConfigFieldMeta[] = [
  // ---- email ----
  {
    path: 'email.provider',
    category: 'email',
    englishLabel: 'Outbound email provider',
    englishHelp: 'Pick how Rynora sends transactional mail (run notifications, password resets, invites).',
    keywords: ['email', 'smtp', 'resend', 'provider', 'mailer', 'outbound'],
    relatedSettings: ['email.smtpHost', 'email.resendApiKey'],
    affectedScreens: ['Run-failure email', 'Password reset email', 'Org invite email'],
  },
  {
    path: 'email.smtpHost',
    category: 'email',
    englishLabel: 'SMTP server hostname',
    englishHelp: 'Hostname of the SMTP server Rynora connects to when the provider is set to SMTP.',
    keywords: ['email', 'smtp', 'host', 'server', 'mail', 'outbound'],
    relatedSettings: ['email.smtpPort', 'email.smtpUser', 'email.smtpSecure'],
  },
  {
    path: 'email.smtpFrom',
    category: 'email',
    englishLabel: 'From address on outbound emails',
    englishHelp: 'The address recipients see in the From header. Must be a domain you own.',
    keywords: ['email', 'from', 'sender', 'reply-to'],
  },
  {
    path: 'email.resendApiKey',
    category: 'email',
    englishLabel: 'Resend API key',
    englishHelp: 'API key for the Resend mail provider when `email.provider` is set to `resend`.',
    keywords: ['email', 'resend', 'api', 'key'],
  },
  // ---- auth ----
  {
    path: 'auth.googleEnabled',
    category: 'auth',
    englishLabel: 'Enable Google sign-in',
    englishHelp: 'Lets users sign in with their Google account via OAuth.',
    keywords: ['auth', 'login', 'sso', 'google', 'oauth', 'sign-in'],
    relatedSettings: ['auth.googleClientId', 'auth.googleClientSecret'],
  },
  {
    path: 'auth.microsoftClientId',
    category: 'auth',
    englishLabel: 'Microsoft (Entra ID) OAuth client id',
    englishHelp: 'The application id for Microsoft sign-in (Azure AD / Entra ID).',
    keywords: ['auth', 'sso', 'microsoft', 'azure', 'entra', 'oauth', 'login'],
  },
  {
    path: 'auth.jwtSecret',
    category: 'auth',
    englishLabel: 'JWT signing secret',
    englishHelp: 'Server-side secret used to sign session JWTs. Rotate carefully — rotating signs out everyone.',
    keywords: ['auth', 'jwt', 'secret', 'session', 'token', 'sign'],
  },
  // ---- ai ----
  {
    path: 'ai.openaiApiKey',
    category: 'ai',
    englishLabel: 'OpenAI API key',
    englishHelp:
      'Used for natural-language step parsing, Story → Scenarios generation, intent engine. BYOK keys override.',
    keywords: ['ai', 'openai', 'api', 'key', 'gpt', 'llm'],
    relatedSettings: ['ai.openaiModel', 'ai.openaiBaseUrl'],
    affectedScreens: ['Story → Scenarios wizard', 'Natural-language step input', 'Healing-explain'],
  },
  {
    path: 'ai.anthropicApiKey',
    category: 'ai',
    englishLabel: 'Anthropic (Claude) API key',
    englishHelp: 'Used when Anthropic is the configured provider for any AI feature.',
    keywords: ['ai', 'anthropic', 'claude', 'api', 'key', 'llm'],
  },
  {
    path: 'ai.groqApiKey',
    category: 'ai',
    englishLabel: 'Groq Cloud API key',
    englishHelp: 'High-speed inference; used when Groq is the configured provider for any AI feature.',
    keywords: ['ai', 'groq', 'api', 'key', 'llm', 'fast'],
  },
  // ---- integrations (Slack lives here) ----
  {
    path: 'integrations.slackEnabled',
    category: 'integrations',
    englishLabel: 'Enable Slack outbound notifications',
    englishHelp:
      'Master kill switch for the Slack integration. When off, Rynora never posts to any Slack webhook regardless of per-channel settings.',
    keywords: ['slack', 'chat', 'webhook', 'notification', 'failure', 'alert'],
    relatedSettings: ['notifications.channels.slack.enabled'],
    affectedScreens: ['Run-failure flow', 'Notifications panel'],
  },
  {
    path: 'integrations.githubAppId',
    category: 'integrations',
    englishLabel: 'GitHub App id (for SCM integration)',
    englishHelp: 'The GitHub App installation id Rynora uses to read pull requests and comment on commits.',
    keywords: ['github', 'scm', 'pr', 'app', 'integration'],
  },
  // ---- jira (lives at the top level once #12 lands; safe to register now) ----
  {
    path: 'jira.enabled',
    category: 'jira',
    englishLabel: 'Open a Jira issue when a run fails',
    englishHelp: 'When enabled and Jira credentials are configured, Rynora opens a Jira issue for every failed run.',
    keywords: ['jira', 'bug', 'issue', 'ticket', 'integration', 'failure'],
    relatedSettings: ['jira.componentMap', 'jira.deduplicationWindowHours'],
  },
  {
    path: 'jira.autoAddToActiveSprint',
    category: 'jira',
    englishLabel: 'Auto-add new issues to the active sprint',
    englishHelp: 'When enabled, Rynora queries the active sprint and attaches the new issue to it.',
    keywords: ['jira', 'sprint', 'scrum', 'agile', 'auto', 'attach'],
  },
  // ---- billing ----
  {
    path: 'billing.stripeSecretKey',
    category: 'billing',
    englishLabel: 'Stripe API secret key',
    englishHelp: 'Used for invoice creation, plan changes, webhook verification. Treat as a high-sensitivity secret.',
    keywords: ['billing', 'stripe', 'payment', 'api', 'key', 'invoice'],
    relatedSettings: ['billing.stripeWebhookSecret'],
  },
  // ---- runner ----
  {
    path: 'runner.maxConcurrentSessions',
    category: 'runner',
    englishLabel: 'Maximum concurrent browser sessions',
    englishHelp: 'Caps how many runs can be in flight simultaneously. Higher = faster suites, more memory.',
    keywords: ['runner', 'parallel', 'concurrency', 'sessions', 'browser', 'speed', 'max'],
  },
  {
    path: 'runner.traceEnabled',
    category: 'runner',
    englishLabel: 'Record Playwright traces on runs',
    englishHelp: 'Global kill-switch for trace capture / serving. Off frees disk + bandwidth at the cost of replay.',
    keywords: ['runner', 'trace', 'playwright', 'capture'],
  },
  // ---- reporting ----
  {
    path: 'reporting.retention.maxAgeDays',
    category: 'reporting',
    englishLabel: 'How many days to keep run reports',
    englishHelp: 'Reports older than this are eligible for the retention worker to delete. Storage compliance lever.',
    keywords: ['reporting', 'retention', 'days', 'cleanup', 'gdpr', 'storage', 'maxAge'],
  },
  // ---- observability ----
  {
    path: 'observability.sentryDsnApi',
    category: 'observability',
    englishLabel: 'Sentry DSN for backend error reporting',
    englishHelp: 'Configure Sentry to receive backend exceptions for triage.',
    keywords: ['observability', 'sentry', 'errors', 'monitoring', 'dsn', 'triage'],
  },
  {
    path: 'server.logLevel',
    category: 'server',
    englishLabel: 'Backend log level',
    englishHelp: 'Pino log level (`debug` / `info` / `warn` / `error`). Default `info`.',
    keywords: ['observability', 'server', 'log', 'level', 'debug', 'verbose', 'pino'],
  },
  // ---- security ----
  {
    path: 'security.flowBuilderIdleLogoutMinutes',
    category: 'security',
    englishLabel: 'Auto-logout idle users after N minutes',
    englishHelp: 'Idle session reaper for compliance environments (SOC2, HIPAA-leaning).',
    keywords: ['security', 'idle', 'logout', 'session', 'soc2', 'compliance'],
    relatedSettings: ['security.flowBuilderIdleWarnMinutes'],
  },
  {
    path: 'security.dast.enabled',
    category: 'security',
    englishLabel: 'Run nightly DAST scan on the production app',
    englishHelp: 'Enables the continuous DAST scanner to crawl the production URL once per day.',
    keywords: ['security', 'dast', 'scan', 'pentest', 'continuous'],
    relatedSettings: ['security.dast.targetUrl', 'security.dast.hourUtc'],
  },
  // ---- ops ----
  {
    path: 'ops.maintenanceMode',
    category: 'ops',
    englishLabel: 'Maintenance mode (block all traffic)',
    englishHelp: 'Returns a friendly maintenance page to all requests. Use during deploys / migrations.',
    keywords: ['ops', 'maintenance', 'downtime', 'deploy', 'kill', 'switch'],
    relatedSettings: ['ops.maintenanceMessage'],
  },
  {
    path: 'ops.flags.runnerEnabled',
    category: 'ops',
    englishLabel: 'Enable the test runner',
    englishHelp: 'Master kill switch for the runner. When off, new runs are queued but not executed.',
    keywords: ['ops', 'runner', 'flag', 'kill', 'switch'],
  },
  {
    path: 'ops.flags.schedulerEnabled',
    category: 'ops',
    englishLabel: 'Enable scheduled (cron) runs',
    englishHelp: 'Master kill switch for the scheduler. When off, scheduled flows do not trigger.',
    keywords: ['ops', 'scheduler', 'cron', 'schedule', 'flag', 'switch'],
  },
  // ---- access ----
  {
    path: 'access.registrationMode',
    category: 'access',
    englishLabel: 'Who can sign up for new accounts',
    englishHelp: '`open` lets anyone register; `invite-only` requires an invite link; `closed` blocks registration.',
    keywords: ['access', 'signup', 'registration', 'invite', 'closed', 'sso-only'],
  },
  // ---- accessibility ----
  {
    path: 'accessibility.contrastWcagLevel',
    category: 'accessibility',
    englishLabel: 'WCAG contrast level for Contrast Advisor',
    englishHelp: 'AA (4.5:1 / 3:1 large) or AAA (7:1 / 4.5:1 large). Default AA.',
    keywords: ['a11y', 'accessibility', 'wcag', 'contrast', 'aa', 'aaa'],
    relatedSettings: ['accessibility.contrastCheckEnabled'],
    affectedScreens: ['Contrast Advisor panel', 'Run report'],
  },
  // ---- mockServer ----
  {
    path: 'mockServer.publicShareLinksEnabled',
    category: 'mockServer',
    englishLabel: 'Allow public share-links for mock servers',
    englishHelp: 'Master kill switch for the unauthenticated mock-server dispatcher. Turn off for compliance.',
    keywords: ['mock', 'server', 'share-link', 'public', 'kill', 'switch'],
    relatedSettings: ['mockServer.proxyAllowlist', 'mockServer.defaultShareLinkRateLimitPerMin'],
  },
  // ---- ai (more) ----
  {
    path: 'ai.openaiModel',
    category: 'ai',
    englishLabel: 'Default OpenAI model',
    englishHelp: 'Which OpenAI model Rynora uses by default (e.g. `gpt-4o-mini`, `gpt-4-turbo`).',
    keywords: ['ai', 'openai', 'model', 'gpt'],
  },
  // ---- reporting (more) ----
  {
    path: 'reporting.emailFormat',
    category: 'reporting',
    englishLabel: 'Run-summary email format (HTML or PDF)',
    englishHelp:
      'Attachment format on the run-summary email. Default HTML. PDF available when business-pdf-reports ships.',
    keywords: ['reporting', 'email', 'pdf', 'html', 'format', 'attachment'],
    relatedSettings: ['reporting.publicShareLinksEnabled'],
  },
  {
    path: 'reporting.publicShareLinksEnabled',
    category: 'reporting',
    englishLabel: 'Allow public share-links on run reports',
    englishHelp: 'Master kill switch for the unauthenticated /reports/share/:token dispatcher. Off for compliance.',
    keywords: ['reporting', 'share-link', 'public', 'kill', 'switch'],
  },
  // ---- cdn ----
  {
    path: 'cdn.cloudflareApiToken',
    category: 'cdn',
    englishLabel: 'Cloudflare API token (cache purge)',
    englishHelp: 'Used to purge the CDN on deploy. Token needs Cache Purge scope on the configured zone.',
    keywords: ['cdn', 'cloudflare', 'purge', 'cache', 'api', 'token'],
    relatedSettings: ['cdn.cloudflareZoneId', 'cdn.purgeOnDeploy'],
  },
  // ---- cloudBrowser ----
  {
    path: 'cloudBrowser.enabled',
    category: 'cloudBrowser',
    englishLabel: 'Run tests on a cloud browser provider',
    englishHelp: 'When enabled, the runner dispatches to the configured cloud provider (Steel, BrowserStack, etc.).',
    keywords: ['cloud', 'browser', 'browserstack', 'steel', 'lambdatest', 'saucelabs', 'remote'],
    relatedSettings: ['cloudBrowser.provider', 'cloudBrowser.browserstackUsername'],
  },
  {
    path: 'cloudBrowser.browserstackUsername',
    category: 'cloudBrowser',
    englishLabel: 'BrowserStack username',
    englishHelp: 'BrowserStack username for the cloud-browser provider integration.',
    keywords: ['browserstack', 'cloud', 'browser', 'username'],
  },
  // ---- secretsManager ----
  {
    path: 'secretsManager.provider',
    category: 'secretsManager',
    englishLabel: 'External secrets-manager provider',
    englishHelp: 'Pick `bitwarden` (or leave blank) to source secrets from an external vault.',
    keywords: ['secrets', 'vault', 'bitwarden', 'provider'],
  },
  // ---- onboarding ----
  {
    path: 'onboarding.firstFlowTutorial.enabled',
    category: 'onboarding',
    englishLabel: 'Show the first-flow tutorial to new users',
    englishHelp: 'When enabled, new users see the interactive ~3-minute first-flow tutorial on their first login.',
    keywords: ['onboarding', 'tutorial', 'first-flow', 'new-user'],
  },
  // ---- noCode ----
  {
    path: 'noCode.describeMode.enabled',
    category: 'noCode',
    englishLabel: 'Enable Describe-mode in the flow editor',
    englishHelp: 'Lets authors describe an element in plain English instead of picking a selector by hand.',
    keywords: ['no-code', 'describe-mode', 'authoring', 'ai', 'element'],
  },
  {
    path: 'noCode.naturalLanguageAuthoring.enabled',
    category: 'noCode',
    englishLabel: 'Enable natural-language step input',
    englishHelp: 'Lets authors type a step in English and have Rynora parse it into structured form.',
    keywords: ['no-code', 'nl', 'natural-language', 'authoring', 'step', 'ai'],
  },
  // ---- approval ----
  {
    path: 'approval.runGate.enabled',
    category: 'approval',
    englishLabel: 'Require approval before a run can promote',
    englishHelp: 'When enabled, runs are held in a "needs-approval" state before their results are promoted.',
    keywords: ['approval', 'gate', 'sign-off', 'review'],
  },
  // ---- notifications ----
  {
    path: 'notifications.channels.slack.enabled',
    category: 'notifications',
    englishLabel: 'Send Slack notifications',
    englishHelp: 'Per-channel toggle for posting any Slack notification (run failure, approval, etc.).',
    keywords: ['slack', 'notification', 'chat', 'channel'],
    relatedSettings: ['integrations.slackEnabled'],
  },
  {
    path: 'notifications.channels.email.enabled',
    category: 'notifications',
    englishLabel: 'Send email notifications',
    englishHelp: 'Per-channel toggle for outbound email notifications.',
    keywords: ['notification', 'email', 'delivery'],
  },
  // ---- editor ----
  {
    path: 'editor.autosaveEnabled',
    category: 'editor',
    englishLabel: 'Auto-save the flow editor canvas',
    englishHelp: 'When enabled, the editor saves every few seconds while you type. Off keeps explicit-save semantics.',
    keywords: ['editor', 'autosave', 'auto-save', 'flow', 'canvas'],
  },
  // ---- theme ----
  {
    path: 'theme.pickedPalette',
    category: 'theme',
    englishLabel: 'Active operator-default palette',
    englishHelp:
      'Slug of the palette applied to every user when they have not personalised their theme. Empty string means "use code defaults".',
    keywords: ['theme', 'palette', 'branding', 'ui', 'colour', 'color'],
  },
  // ---- branding ----
  {
    path: 'branding.supportEmail',
    category: 'branding',
    englishLabel: 'Support email shown in error states',
    englishHelp: 'Surfaced on 5xx error pages + the help footer. Must be a real, monitored inbox.',
    keywords: ['support', 'email', 'help', 'contact', 'branding'],
  },
  {
    path: 'branding.marketingUrl',
    category: 'branding',
    englishLabel: 'Marketing site URL',
    englishHelp:
      'Absolute URL of the public marketing site (production: https://rynora.ai). Empty keeps “Back to home” on the same host (QA / UAT).',
    keywords: ['marketing', 'landing', 'website', 'rynora.ai', 'home', 'branding'],
    relatedSettings: ['auth.appUrl'],
  },
  // ---- storage ----
  {
    path: 'storage.retentionDays',
    category: 'storage',
    englishLabel: 'Days to keep run artifacts (screenshots, videos) in object storage',
    englishHelp: 'Older artifacts are deleted by the storage worker. Lower = cheaper; higher = better triage history.',
    keywords: ['storage', 'retention', 'artifact', 'cleanup', 'days'],
  },
  // ---- payments ----
  {
    path: 'payments.provider',
    category: 'payments',
    englishLabel: 'Active payments provider',
    englishHelp: 'Which payments provider Rynora bills through (Stripe, Razorpay, Paddle, PayPal, Lemonsqueezy).',
    keywords: ['payments', 'provider', 'billing', 'stripe', 'razorpay', 'paddle', 'paypal'],
  },
  // ---- back-fill: fields referenced by scenarios + relatedSettings ----
  {
    path: 'email.smtpPort',
    category: 'email',
    englishLabel: 'SMTP server port',
    englishHelp: 'Port the SMTP server listens on. 25 / 465 / 587 are typical.',
    keywords: ['email', 'smtp', 'port'],
  },
  {
    path: 'email.smtpUser',
    category: 'email',
    englishLabel: 'SMTP username',
    englishHelp: 'Username for the SMTP server when authentication is required.',
    keywords: ['email', 'smtp', 'user', 'username', 'auth'],
  },
  {
    path: 'email.smtpSecure',
    category: 'email',
    englishLabel: 'Use TLS for SMTP',
    englishHelp: 'Connect over implicit TLS (port 465). Off uses STARTTLS or plain.',
    keywords: ['email', 'smtp', 'tls', 'ssl', 'secure'],
  },
  {
    path: 'email.resendFromDomain',
    category: 'email',
    englishLabel: 'Resend verified sending domain',
    englishHelp: 'Verified domain configured in Resend used as the From address.',
    keywords: ['email', 'resend', 'from', 'domain'],
  },
  {
    path: 'ai.openaiBaseUrl',
    category: 'ai',
    englishLabel: 'OpenAI-compatible base URL override',
    englishHelp: 'Optional override for the OpenAI HTTP endpoint (Groq, Together, OpenRouter, vLLM, …).',
    keywords: ['ai', 'openai', 'baseurl', 'endpoint', 'gateway'],
  },
  {
    path: 'auth.googleClientId',
    category: 'auth',
    englishLabel: 'Google OAuth client id',
    englishHelp: 'OAuth client id from Google Cloud Console for SSO sign-in.',
    keywords: ['auth', 'google', 'oauth', 'sso', 'client-id'],
  },
  {
    path: 'auth.googleClientSecret',
    category: 'auth',
    englishLabel: 'Google OAuth client secret',
    englishHelp: 'OAuth client secret paired with the Google client id.',
    keywords: ['auth', 'google', 'oauth', 'sso', 'secret'],
  },
  {
    path: 'auth.microsoftClientSecret',
    category: 'auth',
    englishLabel: 'Microsoft (Entra ID) OAuth client secret',
    englishHelp: 'OAuth client secret for the Microsoft Entra ID application.',
    keywords: ['auth', 'microsoft', 'azure', 'entra', 'oauth', 'secret'],
  },
  {
    path: 'auth.microsoftTenant',
    category: 'auth',
    englishLabel: 'Microsoft (Entra ID) tenant',
    englishHelp: 'Tenant id (or `common`/`organizations`) for the Microsoft Entra ID app.',
    keywords: ['auth', 'microsoft', 'azure', 'entra', 'tenant'],
  },
  {
    path: 'security.dast.targetUrl',
    category: 'security',
    englishLabel: 'DAST target URL',
    englishHelp: 'Production URL the DAST scanner crawls nightly.',
    keywords: ['security', 'dast', 'url', 'target', 'scan'],
  },
  {
    path: 'security.dast.hourUtc',
    category: 'security',
    englishLabel: 'Hour of day (UTC) to run the DAST scan',
    englishHelp: 'Integer 0–23. Pick a low-traffic window for the production app.',
    keywords: ['security', 'dast', 'hour', 'schedule', 'utc'],
  },
  {
    path: 'security.flowBuilderIdleWarnMinutes',
    category: 'security',
    englishLabel: 'Show idle warning after N minutes',
    englishHelp: 'Soft warning before the hard auto-logout fires.',
    keywords: ['security', 'idle', 'warn', 'session'],
  },
  {
    path: 'jira.componentMap',
    category: 'jira',
    englishLabel: 'Flow-tag → Jira component map',
    englishHelp: 'Maps Rynora flow tags onto Jira components when an issue is opened.',
    keywords: ['jira', 'component', 'tag', 'mapping'],
  },
  {
    path: 'jira.deduplicationWindowHours',
    category: 'jira',
    englishLabel: 'Re-failure dedup window (hours)',
    englishHelp: 'Within this window, a re-failure adds a comment to the existing issue instead of opening a new one.',
    keywords: ['jira', 'dedup', 'duplicate', 'window', 'hours'],
  },
  {
    path: 'jira.autoCloseOnGreenStreak',
    category: 'jira',
    englishLabel: 'Auto-close Jira issue when flow goes green',
    englishHelp:
      'If a flow that previously opened a Jira issue passes the next N consecutive runs (configured by `Green streak length`), the hook adds a comment and (optional) transitions the issue to Done.',
    keywords: ['jira', 'close', 'pass', 'green', 'streak', 'resolve'],
    relatedSettings: ['jira.greenStreakLength', 'jira.enabled'],
  },
  {
    path: 'jira.greenStreakLength',
    category: 'jira',
    englishLabel: 'Green streak length (runs)',
    englishHelp:
      'Number of consecutive PASS runs required before the hook acts on `Auto-close Jira issue when flow goes green`.',
    keywords: ['jira', 'streak', 'pass', 'consecutive'],
    relatedSettings: ['jira.autoCloseOnGreenStreak'],
  },
  {
    path: 'jira.defaultAssigneeStrategy',
    category: 'jira',
    englishLabel: 'Default assignee strategy',
    englishHelp:
      'How the hook picks an assignee on issue creation. `none` leaves it unassigned, `project_lead` uses Jira’s configured project lead, `flow_owner_scim` looks up the flow owner’s Jira account via SCIM.',
    keywords: ['jira', 'assignee', 'owner', 'scim', 'lead'],
  },
  {
    path: 'jira.labelPrefix',
    category: 'jira',
    englishLabel: 'Auto-applied Jira label prefix',
    englishHelp:
      'Every auto-created issue carries `<prefix>` + `<prefix>-auto`, plus the flow’s tags. Default `rynora`.',
    keywords: ['jira', 'label', 'tag', 'prefix'],
  },
  {
    path: 'jira.rateLimitPerHour',
    category: 'jira',
    englishLabel: 'Jira hook request budget (per hour)',
    englishHelp:
      'In-process token-bucket per org. A burst of failures will not exceed this many Jira requests per hour. Cloud free tier caps at 200/hr — keep below.',
    keywords: ['jira', 'rate', 'limit', 'throttle', 'token-bucket'],
  },
  {
    path: 'jira.priorityMap',
    category: 'jira',
    englishLabel: 'Severity → Jira priority map',
    englishHelp:
      'Maps Rynora-computed severities (`critical`, `major`, `minor`, `trivial`) onto Jira priority names. Default maps to `Highest`, `High`, `Medium`, `Low`.',
    keywords: ['jira', 'priority', 'severity', 'mapping'],
  },
  {
    path: 'jira.epicTagPattern',
    category: 'jira',
    englishLabel: 'Epic-tag regex',
    englishHelp:
      'Regex applied to a flow’s tags. The first capture group must yield a Jira epic key (e.g. `epic-CHECK-123` → `CHECK-123`). Default `^epic-([A-Z]+-\\d+)$`.',
    keywords: ['jira', 'epic', 'tag', 'regex', 'pattern'],
  },
  {
    path: 'billing.stripeWebhookSecret',
    category: 'billing',
    englishLabel: 'Stripe webhook signing secret',
    englishHelp: 'Verifies that incoming Stripe webhooks are authentic. Rotates on demand from the Stripe dashboard.',
    keywords: ['billing', 'stripe', 'webhook', 'secret', 'signing'],
  },
  {
    path: 'cloudBrowser.provider',
    category: 'cloudBrowser',
    englishLabel: 'Cloud-browser provider',
    englishHelp:
      'Which remote-browser service Rynora dispatches runs to (steel / browserstack / saucelabs / lambdatest).',
    keywords: ['cloud', 'browser', 'provider', 'browserstack', 'steel', 'saucelabs', 'lambdatest'],
  },
  {
    path: 'cdn.cloudflareZoneId',
    category: 'cdn',
    englishLabel: 'Cloudflare zone id',
    englishHelp: 'Zone id from the Cloudflare dashboard for the deployed app.',
    keywords: ['cdn', 'cloudflare', 'zone', 'id'],
  },
  {
    path: 'cdn.purgeOnDeploy',
    category: 'cdn',
    englishLabel: 'Purge the CDN cache on deploy',
    englishHelp: 'When enabled, every deploy issues a Cloudflare cache-purge call.',
    keywords: ['cdn', 'cloudflare', 'purge', 'cache', 'deploy'],
  },
  {
    path: 'ops.maintenanceMessage',
    category: 'ops',
    englishLabel: 'Maintenance-mode page message',
    englishHelp: 'Text shown to users while maintenanceMode is on.',
    keywords: ['ops', 'maintenance', 'message', 'page'],
  },
  {
    path: 'approval.notifications.email.enabled',
    category: 'approval',
    englishLabel: 'Email approvers when a run needs approval',
    englishHelp: 'Sends an email to the configured approver group when a run hits the approval gate.',
    keywords: ['approval', 'email', 'notification'],
  },
  {
    path: 'accessibility.contrastCheckEnabled',
    category: 'accessibility',
    englishLabel: 'Run the Contrast Advisor on every run',
    englishHelp: 'Master toggle. When off, Contrast Advisor produces no violations regardless of WCAG level.',
    keywords: ['a11y', 'accessibility', 'contrast', 'enabled', 'toggle'],
    relatedSettings: ['accessibility.contrastWcagLevel'],
  },
  {
    path: 'mockServer.proxyAllowlist',
    category: 'mockServer',
    englishLabel: 'Mock-server proxy-mode upstream allowlist',
    englishHelp: 'Whitelist of upstream URLs the mock server is allowed to proxy to in proxy mode.',
    keywords: ['mock', 'server', 'proxy', 'allowlist', 'whitelist'],
  },
  {
    path: 'mockServer.defaultShareLinkRateLimitPerMin',
    category: 'mockServer',
    englishLabel: 'Default mock share-link rate limit (req/min)',
    englishHelp: 'Per-token request cap applied to public mock-server share-links.',
    keywords: ['mock', 'server', 'rate-limit', 'share-link'],
  },
  // -----------------------------------------------------------------
  // v2 backfill batch (high-priority gaps surfaced by the coverage
  // gate). Keep these grouped by tab so the sidebar in the help-side
  // panel feels coherent. Anything not in this list is still reachable
  // via the auto-derived merged registry — but the rows below pass the
  // strict gate.
  // -----------------------------------------------------------------
  // ---- integrations (kill switches that operators flip on day 1) ----
  {
    path: 'integrations.githubAppEnabled',
    category: 'integrations',
    englishLabel: 'Enable the Rynora GitHub App',
    englishHelp:
      'Master kill switch for the GitHub App. When off, no GitHub OAuth, PR check runs, or commit-status posts happen.',
    keywords: ['github', 'integration', 'app', 'oauth', 'pr', 'commit'],
    relatedSettings: ['integrations.githubAppId', 'integrations.githubAppPrivateKey', 'integrations.githubAppSlug'],
  },
  {
    path: 'integrations.jiraEnabled',
    category: 'integrations',
    englishLabel: 'Enable Jira integration',
    englishHelp:
      'Master kill switch for Jira OAuth + hook. When off, the "Open Jira on failure" hook stops firing regardless of per-monitor config.',
    keywords: ['jira', 'integration', 'oauth', 'hook', 'ticket'],
    relatedSettings: ['integrations.jiraClientId', 'integrations.jiraClientSecret', 'jira.enabled'],
  },
  {
    path: 'integrations.samlEnabled',
    category: 'integrations',
    englishLabel: 'Enable SAML 2.0 single sign-on',
    englishHelp:
      'Lets the org accept SAML assertions from an IdP (Okta, Azure AD, OneLogin, etc.). Requires uploading the IdP metadata XML on the SSO page.',
    keywords: ['saml', 'sso', 'idp', 'okta', 'azure', 'enterprise'],
  },
  {
    path: 'integrations.oidcEnabled',
    category: 'integrations',
    englishLabel: 'Enable OIDC single sign-on',
    englishHelp: 'Lets the org accept OpenID Connect logins. Use this when your IdP speaks OIDC rather than SAML.',
    keywords: ['oidc', 'sso', 'openid', 'idp', 'enterprise'],
  },
  {
    path: 'integrations.outboundWebhooksEnabled',
    category: 'integrations',
    englishLabel: 'Enable outbound webhooks',
    englishHelp:
      'Master kill switch for every outbound webhook surface (monitors, notifications, run lifecycle). Useful for compliance lockdown.',
    keywords: ['webhook', 'outbound', 'integration', 'compliance', 'lockdown'],
  },
  {
    path: 'integrations.adoEnabled',
    category: 'integrations',
    englishLabel: 'Enable Azure DevOps integration',
    englishHelp:
      'Lets users connect Azure DevOps work items as failure-target tickets. Per-org credentials live in the ado_integrations table.',
    keywords: ['ado', 'azure', 'devops', 'integration', 'ticket'],
  },
  {
    path: 'integrations.rallyEnabled',
    category: 'integrations',
    englishLabel: 'Enable Rally / Broadcom ALM integration',
    englishHelp:
      'Lets users connect Rally (CA Agile Central) for ticket creation. Per-org credentials in rally_integrations.',
    keywords: ['rally', 'broadcom', 'ca', 'agile', 'alm', 'integration'],
  },
  // ---- auth ----
  {
    path: 'auth.githubEnabled',
    category: 'auth',
    englishLabel: 'Enable GitHub sign-in',
    englishHelp: 'Lets users sign in with their GitHub account via OAuth.',
    keywords: ['auth', 'github', 'sso', 'oauth', 'sign-in'],
    relatedSettings: ['auth.githubClientId', 'auth.githubClientSecret'],
  },
  {
    path: 'auth.microsoftEnabled',
    category: 'auth',
    englishLabel: 'Enable Microsoft sign-in',
    englishHelp: 'Lets users sign in with their Microsoft (Entra ID / Azure AD) account via OAuth.',
    keywords: ['auth', 'microsoft', 'azure', 'entra', 'sso', 'oauth'],
    relatedSettings: ['auth.microsoftClientId', 'auth.microsoftClientSecret'],
  },
  {
    path: 'auth.jwtExpiresIn',
    category: 'auth',
    englishLabel: 'Session JWT lifetime',
    englishHelp:
      'How long a logged-in session lasts before the user is forced to sign in again. Accepts values like "7d" or "12h".',
    keywords: ['auth', 'jwt', 'session', 'expiry', 'timeout'],
  },
  {
    path: 'auth.rateLimitAuthMax',
    category: 'auth',
    englishLabel: 'Max sign-in attempts per IP / window',
    englishHelp:
      'Sliding-window rate limit applied to login + password-reset endpoints. Lower values harden against credential stuffing.',
    keywords: ['auth', 'rate-limit', 'login', 'brute-force', 'security'],
  },
  // ---- ai (per-feature overrides) ----
  {
    path: 'ai.orgByokEnabled',
    category: 'ai',
    englishLabel: 'Let orgs bring their own AI keys (BYOK)',
    englishHelp:
      'When on, an org can paste its own OpenAI / Anthropic / Groq key on its settings page and override the platform-wide key for every AI feature.',
    keywords: ['ai', 'byok', 'org', 'api', 'key'],
  },
  {
    path: 'ai.streamingEnabled',
    category: 'ai',
    englishLabel: 'Enable Intent parse-stream (soak)',
    englishHelp:
      'Kill switch for POST /api/intent/parse-stream (and /api/v1). Soft-deprecated — production UI uses non-stream /parse. Default on during soak; set false → 503 after zero external traffic is proven. Does not affect /parse.',
    keywords: ['ai', 'streaming', 'intent', 'parse-stream', 'soak', 'deprecation'],
  },
  {
    path: 'ai.intentPhase5OrphansEnabled',
    category: 'ai',
    englishLabel: 'Enable Phase-5 Intent orphan APIs (soak)',
    englishHelp:
      'Kill switch for soft-deprecated POST /api/intent/{suggest,rca,semantic-assert,adversarial,explore,detect-dynamic}. Default on during soak until operators prove zero external traffic. Set false → HTTP 410. Does not affect /parse, /explain, /vision, or /tool-call.',
    keywords: ['ai', 'intent', 'phase5', 'orphan', 'soak', '410', 'deprecation'],
    relatedSettings: ['ai.streamingEnabled', 'ai.toolUseEnabled'],
  },
  {
    path: 'ai.visionEnabled',
    category: 'ai',
    englishLabel: 'Enable AI vision (screenshots → analysis)',
    englishHelp: 'When on, AI features can attach run screenshots to the model for visual debugging and RCA.',
    keywords: ['ai', 'vision', 'screenshot', 'multimodal', 'rca'],
  },
  {
    path: 'ai.anthropicModel',
    category: 'ai',
    englishLabel: 'Default Anthropic model',
    englishHelp: 'Model id used when Anthropic is the provider for any AI feature (e.g. `claude-3-5-sonnet`).',
    keywords: ['ai', 'anthropic', 'claude', 'model'],
  },
  {
    path: 'ai.groqModel',
    category: 'ai',
    englishLabel: 'Default Groq model',
    englishHelp: 'Model id used when Groq is the provider for any AI feature (e.g. `llama-3.1-70b-versatile`).',
    keywords: ['ai', 'groq', 'model', 'llama'],
  },
  // ---- monitors (the v1.1 leaves that should have been registered) ----
  {
    path: 'monitors.enabled',
    category: 'monitors',
    englishLabel: 'Enable synthetic monitors',
    englishHelp:
      'Master kill switch for synthetic monitoring. When off, no scheduled monitor flows run and no pages fire regardless of per-monitor config.',
    keywords: ['monitor', 'synthetic', 'enabled', 'killswitch'],
  },
  {
    path: 'monitors.defaultConsecutiveFailuresToPage',
    category: 'monitors',
    englishLabel: 'Default failures before paging',
    englishHelp:
      'How many consecutive failed runs must occur before a monitor escalates to "down" and fires its pages.',
    keywords: ['monitor', 'failure', 'threshold', 'page', 'escalation'],
  },
  {
    path: 'monitors.publicBadgesEnabled',
    category: 'monitors',
    englishLabel: 'Allow public status badges',
    englishHelp:
      'Org-wide kill switch for the Statuspage-style SVG badge endpoint. When off, the public `/api/monitors/badge/:token.svg` endpoint returns 403.',
    keywords: ['monitor', 'badge', 'public', 'svg', 'statuspage'],
  },
  {
    path: 'monitors.badgeRateLimitPerMin',
    category: 'monitors',
    englishLabel: 'Public badge rate limit (req/min)',
    englishHelp:
      'Per-token sliding-window cap for the public badge endpoint. Higher values support embedding on busier pages.',
    keywords: ['monitor', 'badge', 'rate-limit', 'public'],
  },
  // ---- reporting (high-traffic surface) ----
  {
    path: 'reporting.enabled',
    category: 'reporting',
    englishLabel: 'Enable run reports',
    englishHelp:
      'Master toggle for generating run-level HTML / PDF reports. When off, runs still execute but no report is built.',
    keywords: ['reporting', 'reports', 'enabled', 'killswitch'],
  },
  {
    path: 'reporting.outputDir',
    category: 'reporting',
    englishLabel: 'On-disk directory for stored reports',
    englishHelp:
      'Server-side path where run-report HTML is written. Must be a persistent volume (not /tmp) so reports survive API restarts. Docker sets RYNORA_REPORTS_DIR=/var/lib/rynora/reports.',
    keywords: ['reporting', 'output', 'directory', 'storage', 'path', 'reports'],
  },
  {
    path: 'reporting.exportDir',
    category: 'reporting',
    englishLabel: 'On-disk directory for PDF / report exports',
    englishHelp:
      'Server-side path where exported PDFs and share-link bundles are staged before upload. Separate from `reporting.outputDir` so the cleanup sweep can target each independently.',
    keywords: ['reporting', 'export', 'pdf', 'directory', 'storage', 'path'],
    relatedSettings: ['reporting.outputDir'],
  },
  // ---- notifications ----
  {
    path: 'notifications.enabled',
    category: 'notifications',
    englishLabel: 'Enable outbound notifications',
    englishHelp:
      'Master kill switch across every notification channel (email, Slack, Teams, Discord, webhook). When off, no outbound notification fires.',
    keywords: ['notification', 'enabled', 'master', 'killswitch'],
  },
  {
    path: 'notifications.defaultSenderEmail',
    category: 'notifications',
    englishLabel: 'Default From address for notifications',
    englishHelp: 'From address used on outbound notification emails when a template does not override it.',
    keywords: ['notification', 'email', 'sender', 'from'],
  },
  // ---- notifications (v2 backfill: branding + categories + channels + delivery + marketing) ----
  {
    path: 'notifications.defaultSenderName',
    category: 'notifications',
    englishLabel: 'Default sender name on notifications',
    englishHelp: 'The visible "name" portion of the From line on outbound notification emails (e.g. "Rynora Alerts").',
    keywords: ['notification', 'sender', 'name', 'from', 'branding'],
  },
  {
    path: 'notifications.replyToEmail',
    category: 'notifications',
    englishLabel: 'Reply-to address on notifications',
    englishHelp: 'Where replies land when a recipient hits Reply on a notification email. Leave blank to drop replies.',
    keywords: ['notification', 'reply', 'inbox', 'support'],
  },
  {
    path: 'notifications.physicalAddress',
    category: 'notifications',
    englishLabel: 'Physical mailing address (CAN-SPAM / GDPR)',
    englishHelp:
      'Postal address rendered in the footer of marketing emails to satisfy CAN-SPAM + GDPR transparency rules.',
    keywords: ['notification', 'marketing', 'compliance', 'address', 'footer', 'spam'],
  },
  {
    path: 'notifications.unsubscribeTokenSecret',
    category: 'notifications',
    englishLabel: 'Unsubscribe-link signing secret',
    englishHelp:
      'Server-side secret used to sign one-click unsubscribe links. Rotate carefully — rotating invalidates every live link.',
    keywords: ['notification', 'unsubscribe', 'secret', 'token', 'sign', 'rotate'],
  },
  {
    path: 'notifications.brandingLogoUrl',
    category: 'notifications',
    englishLabel: 'Logo URL on notification emails',
    englishHelp: 'Public URL of the logo rendered at the top of every notification email. Must be HTTPS.',
    keywords: ['notification', 'branding', 'logo', 'email', 'header'],
    relatedSettings: ['notifications.brandingPrimaryColor', 'branding.supportEmail'],
  },
  {
    path: 'notifications.brandingPrimaryColor',
    category: 'notifications',
    englishLabel: 'Primary brand colour on notification emails',
    englishHelp: 'Hex colour used for headings and CTA buttons in notification emails.',
    keywords: ['notification', 'branding', 'colour', 'color', 'theme'],
  },
  {
    path: 'notifications.templateOverrides',
    category: 'notifications',
    englishLabel: 'Per-template subject / body overrides',
    englishHelp:
      'JSON map keyed by template id. Lets operators override subject lines and HTML bodies without a deploy.',
    keywords: ['notification', 'template', 'override', 'subject', 'body'],
  },
  {
    path: 'notifications.delivery.retryAttempts',
    category: 'notifications',
    englishLabel: 'Delivery retry attempt count',
    englishHelp: 'Number of times to retry a failed notification dispatch before parking it in the dead-letter queue.',
    keywords: ['notification', 'delivery', 'retry', 'attempts', 'resilience'],
    relatedSettings: ['notifications.delivery.retryBackoffSeconds', 'notifications.delivery.deadLetterAfterAttempts'],
  },
  {
    path: 'notifications.delivery.retryBackoffSeconds',
    category: 'notifications',
    englishLabel: 'Delivery retry backoff (seconds)',
    englishHelp:
      'Exponential-backoff base delay between retry attempts. Final attempt fires at `base × 2^(attempt-1)` seconds.',
    keywords: ['notification', 'delivery', 'retry', 'backoff', 'delay'],
  },
  {
    path: 'notifications.delivery.deadLetterAfterAttempts',
    category: 'notifications',
    englishLabel: 'Dead-letter after N attempts',
    englishHelp: 'Move a notification to the dead-letter queue once delivery has been retried this many times.',
    keywords: ['notification', 'delivery', 'dead-letter', 'dlq', 'parked'],
  },
  {
    path: 'notifications.delivery.ratePerHourGlobal',
    category: 'notifications',
    englishLabel: 'Global outbound notification rate (per hour)',
    englishHelp:
      'Cap on total notifications the org can send per hour across every channel. Protects against runaway loops.',
    keywords: ['notification', 'delivery', 'rate', 'throttle', 'limit', 'global'],
  },
  {
    path: 'notifications.delivery.ratePerHourPerUser',
    category: 'notifications',
    englishLabel: 'Per-user notification rate (per hour)',
    englishHelp: 'Cap on notifications delivered to a single recipient per hour. Prevents one user being flooded.',
    keywords: ['notification', 'delivery', 'rate', 'throttle', 'user'],
  },
  {
    path: 'notifications.delivery.honourQuietHours',
    category: 'notifications',
    englishLabel: 'Respect per-user quiet hours',
    englishHelp:
      'When on, non-urgent notifications are queued until the recipient is outside their quiet-hours window.',
    keywords: ['notification', 'delivery', 'quiet', 'hours', 'sleep', 'do-not-disturb'],
  },
  {
    path: 'notifications.categories.transactional.defaultChannels',
    category: 'notifications',
    englishLabel: 'Transactional channels (default)',
    englishHelp:
      'Channels (email / in-app / web-push) used for transactional notifications when no per-user override exists.',
    keywords: ['notification', 'transactional', 'channels', 'default', 'category'],
  },
  {
    path: 'notifications.categories.transactional.userOptOutAllowed',
    category: 'notifications',
    englishLabel: 'Allow opt-out of transactional notifications',
    englishHelp:
      'Most jurisdictions require transactional notifications to be deliverable; defaults OFF to keep them unblockable.',
    keywords: ['notification', 'transactional', 'opt-out', 'category', 'compliance'],
  },
  {
    path: 'notifications.categories.account.defaultChannels',
    category: 'notifications',
    englishLabel: 'Account-event channels (default)',
    englishHelp: 'Channels used for account notifications (password resets, MFA enrolment, login from new device).',
    keywords: ['notification', 'account', 'channels', 'default'],
  },
  {
    path: 'notifications.categories.account.userOptOutAllowed',
    category: 'notifications',
    englishLabel: 'Allow opt-out of account notifications',
    englishHelp: 'When OFF, security-relevant account events always reach the user. Recommended default.',
    keywords: ['notification', 'account', 'opt-out', 'security'],
  },
  {
    path: 'notifications.categories.billing.defaultChannels',
    category: 'notifications',
    englishLabel: 'Billing channels (default)',
    englishHelp: 'Channels used for billing notifications (invoice issued, card expiring, payment failed).',
    keywords: ['notification', 'billing', 'channels', 'default', 'invoice', 'payment'],
  },
  {
    path: 'notifications.categories.billing.userOptOutAllowed',
    category: 'notifications',
    englishLabel: 'Allow opt-out of billing notifications',
    englishHelp:
      'Recommended OFF — billing notifications often need to be delivered to satisfy contractual / tax obligations.',
    keywords: ['notification', 'billing', 'opt-out'],
  },
  {
    path: 'notifications.categories.operational.defaultChannels',
    category: 'notifications',
    englishLabel: 'Operational channels (default)',
    englishHelp: 'Channels used for operational notifications (run failed, monitor degraded, schedule paused).',
    keywords: ['notification', 'operational', 'channels', 'default', 'ops'],
  },
  {
    path: 'notifications.categories.operational.userOptOutAllowed',
    category: 'notifications',
    englishLabel: 'Allow opt-out of operational notifications',
    englishHelp: 'When ON, users can mute run/monitor failure pings. Keep OFF for on-call rotations.',
    keywords: ['notification', 'operational', 'opt-out', 'ops'],
  },
  {
    path: 'notifications.categories.product.defaultChannels',
    category: 'notifications',
    englishLabel: 'Product update channels (default)',
    englishHelp: 'Channels used for product notifications (new feature, beta invite, in-app announcement).',
    keywords: ['notification', 'product', 'channels', 'updates'],
  },
  {
    path: 'notifications.categories.product.userOptOutAllowed',
    category: 'notifications',
    englishLabel: 'Allow opt-out of product notifications',
    englishHelp: 'Recommended ON so users can mute product announcements without losing operational pings.',
    keywords: ['notification', 'product', 'opt-out'],
  },
  {
    path: 'notifications.categories.marketing.defaultChannels',
    category: 'notifications',
    englishLabel: 'Marketing channels (default)',
    englishHelp: 'Channels used for marketing notifications (newsletter, webinar invite, sales touch).',
    keywords: ['notification', 'marketing', 'channels', 'default'],
  },
  {
    path: 'notifications.categories.marketing.userOptOutAllowed',
    category: 'notifications',
    englishLabel: 'Allow opt-out of marketing notifications',
    englishHelp: 'Must be ON to satisfy CAN-SPAM / GDPR — marketing notifications must always be opt-out-able.',
    keywords: ['notification', 'marketing', 'opt-out', 'compliance', 'gdpr', 'spam'],
  },
  {
    path: 'notifications.categories.digest.defaultChannels',
    category: 'notifications',
    englishLabel: 'Digest channels (default)',
    englishHelp: 'Channels used for daily / weekly digest notifications (run summary, flake roundup, perf trend).',
    keywords: ['notification', 'digest', 'summary', 'weekly', 'daily'],
  },
  {
    path: 'notifications.categories.digest.userOptOutAllowed',
    category: 'notifications',
    englishLabel: 'Allow opt-out of digest notifications',
    englishHelp: 'Recommended ON so users can mute weekly digests without losing transactional or operational pings.',
    keywords: ['notification', 'digest', 'opt-out'],
  },
  {
    path: 'notifications.channels.slack.allowedHostSuffixes',
    category: 'notifications',
    englishLabel: 'Slack webhook allow-listed host suffixes',
    englishHelp:
      'Whitelisted host suffixes for Slack incoming webhooks (typically `hooks.slack.com`). Blocks accidental SSRF.',
    keywords: ['notification', 'slack', 'webhook', 'security', 'ssrf', 'allowlist'],
  },
  {
    path: 'notifications.channels.teams.enabled',
    category: 'notifications',
    englishLabel: 'Enable Microsoft Teams channel',
    englishHelp: 'Master switch for the Microsoft Teams incoming-webhook adapter.',
    keywords: ['notification', 'teams', 'microsoft', 'channel', 'enabled'],
  },
  {
    path: 'notifications.channels.teams.allowedHostSuffixes',
    category: 'notifications',
    englishLabel: 'Teams webhook allow-listed host suffixes',
    englishHelp: 'Whitelisted host suffixes for Teams webhooks (typically `webhook.office.com`).',
    keywords: ['notification', 'teams', 'webhook', 'security', 'allowlist'],
  },
  {
    path: 'notifications.channels.discord.enabled',
    category: 'notifications',
    englishLabel: 'Enable Discord channel',
    englishHelp: 'Master switch for the Discord webhook adapter.',
    keywords: ['notification', 'discord', 'channel', 'enabled'],
  },
  {
    path: 'notifications.channels.discord.allowedHostSuffixes',
    category: 'notifications',
    englishLabel: 'Discord webhook allow-listed host suffixes',
    englishHelp: 'Whitelisted host suffixes for Discord webhooks (typically `discord.com`).',
    keywords: ['notification', 'discord', 'webhook', 'security', 'allowlist'],
  },
  {
    path: 'notifications.channels.genericWebhook.enabled',
    category: 'notifications',
    englishLabel: 'Enable generic outbound webhooks',
    englishHelp: 'Master switch for the generic JSON webhook adapter. Disable to block all webhook outbound traffic.',
    keywords: ['notification', 'webhook', 'generic', 'enabled', 'outbound'],
  },
  {
    path: 'notifications.channels.genericWebhook.allowedHostSuffixes',
    category: 'notifications',
    englishLabel: 'Generic webhook allow-listed host suffixes',
    englishHelp: 'Whitelisted host suffixes for generic webhooks. Empty means "allow any" — set this in production.',
    keywords: ['notification', 'webhook', 'security', 'ssrf', 'allowlist'],
  },
  {
    path: 'notifications.channels.webPush.enabled',
    category: 'notifications',
    englishLabel: 'Enable web-push notifications',
    englishHelp: 'Master switch for browser Web Push notifications. Requires valid VAPID keys.',
    keywords: ['notification', 'webpush', 'browser', 'push'],
    relatedSettings: [
      'notifications.channels.webPush.vapidPublicKey',
      'notifications.channels.webPush.vapidPrivateKey',
    ],
  },
  {
    path: 'notifications.channels.webPush.vapidPublicKey',
    category: 'notifications',
    englishLabel: 'Web-push VAPID public key',
    englishHelp: 'Public half of the VAPID keypair used by browsers to verify push messages. Shipped to the SPA.',
    keywords: ['notification', 'webpush', 'vapid', 'public', 'key'],
  },
  {
    path: 'notifications.channels.webPush.vapidPrivateKey',
    category: 'notifications',
    englishLabel: 'Web-push VAPID private key',
    englishHelp: 'Private half of the VAPID keypair. Never leaves the server; used to sign outbound push messages.',
    keywords: ['notification', 'webpush', 'vapid', 'private', 'secret'],
  },
  {
    path: 'notifications.channels.webPush.vapidSubject',
    category: 'notifications',
    englishLabel: 'Web-push VAPID subject',
    englishHelp: 'Contact URL or `mailto:` registered with the push service. Browsers may use it for abuse reporting.',
    keywords: ['notification', 'webpush', 'vapid', 'subject', 'contact'],
  },
  {
    path: 'notifications.marketing.enabled',
    category: 'notifications',
    englishLabel: 'Enable marketing notifications',
    englishHelp:
      'Master switch for the marketing category. When off, no marketing message is dispatched regardless of channel.',
    keywords: ['notification', 'marketing', 'enabled', 'master'],
  },
  {
    path: 'notifications.marketing.requireDoubleOptIn',
    category: 'notifications',
    englishLabel: 'Require double opt-in for marketing',
    englishHelp:
      'When on, a marketing subscription only activates after the user clicks a confirmation link. Recommended for EU/UK.',
    keywords: ['notification', 'marketing', 'opt-in', 'double', 'gdpr', 'compliance'],
  },
  {
    path: 'notifications.marketing.openTrackingEnabled',
    category: 'notifications',
    englishLabel: 'Track marketing-email opens',
    englishHelp:
      'When on, marketing emails carry a tracking pixel that records opens. Disable for privacy-sensitive cohorts.',
    keywords: ['notification', 'marketing', 'tracking', 'open', 'pixel'],
  },
  {
    path: 'notifications.marketing.clickTrackingEnabled',
    category: 'notifications',
    englishLabel: 'Track marketing-email clicks',
    englishHelp: 'When on, marketing links are rewritten through a tracker URL that records clicks before redirecting.',
    keywords: ['notification', 'marketing', 'tracking', 'click', 'link'],
  },
  {
    path: 'notifications.marketing.senderDomain',
    category: 'notifications',
    englishLabel: 'Marketing sender domain',
    englishHelp:
      'Domain used in the marketing From address. Should differ from the transactional domain to protect deliverability.',
    keywords: ['notification', 'marketing', 'domain', 'sender', 'deliverability'],
  },
  {
    path: 'notifications.marketing.suppressionListVisible',
    category: 'notifications',
    englishLabel: 'Show suppression list in admin',
    englishHelp:
      'When on, an admin can inspect (but not bypass) the marketing-suppression list to confirm unsubscribes.',
    keywords: ['notification', 'marketing', 'suppression', 'unsubscribe', 'visibility'],
  },
  // ---- payments (v2 backfill: per-provider plans + credentials) ----
  {
    path: 'payments.enabled',
    category: 'payments',
    englishLabel: 'Enable billing / payments',
    englishHelp:
      'Master switch for the entire payments subsystem. When off, no checkout / subscription / invoice flows are exposed.',
    keywords: ['payments', 'billing', 'enabled', 'master', 'killswitch'],
  },
  {
    path: 'payments.manualInstructions',
    category: 'payments',
    englishLabel: 'Manual-payment instructions (text)',
    englishHelp:
      'Plain-text invoice instructions shown to customers when manual ACH / wire / cheque payments are configured.',
    keywords: ['payments', 'manual', 'ach', 'wire', 'instructions', 'invoice'],
  },
  {
    path: 'payments.manualOpsEmail',
    category: 'payments',
    englishLabel: 'Manual-payments ops email',
    englishHelp: 'Where notifications fire when a manual payment is logged or needs reconciliation.',
    keywords: ['payments', 'manual', 'ops', 'email', 'reconcile'],
  },
  {
    path: 'payments.lemonsqueezyApiKey',
    category: 'payments',
    englishLabel: 'Lemon Squeezy API key',
    englishHelp: 'API key used to issue Lemon Squeezy checkout sessions and verify webhooks.',
    keywords: ['payments', 'lemonsqueezy', 'api', 'key'],
    relatedSettings: ['payments.lemonsqueezyStoreId', 'payments.lemonsqueezyWebhookSecret'],
  },
  {
    path: 'payments.lemonsqueezyStoreId',
    category: 'payments',
    englishLabel: 'Lemon Squeezy store id',
    englishHelp: 'Store id where Rynora subscriptions live in the Lemon Squeezy dashboard.',
    keywords: ['payments', 'lemonsqueezy', 'store', 'id'],
  },
  {
    path: 'payments.lemonsqueezyWebhookSecret',
    category: 'payments',
    englishLabel: 'Lemon Squeezy webhook secret',
    englishHelp: 'Shared secret used to verify HMAC signatures on incoming Lemon Squeezy webhooks.',
    keywords: ['payments', 'lemonsqueezy', 'webhook', 'secret', 'hmac'],
  },
  {
    path: 'payments.lemonsqueezyVariants.starter',
    category: 'payments',
    englishLabel: 'Lemon Squeezy variant — Starter',
    englishHelp: 'Lemon Squeezy variant id mapped to the Starter plan tier.',
    keywords: ['payments', 'lemonsqueezy', 'variant', 'starter', 'plan'],
  },
  {
    path: 'payments.lemonsqueezyVariants.pro',
    category: 'payments',
    englishLabel: 'Lemon Squeezy variant — Pro',
    englishHelp: 'Lemon Squeezy variant id mapped to the Pro plan tier.',
    keywords: ['payments', 'lemonsqueezy', 'variant', 'pro', 'plan'],
  },
  {
    path: 'payments.lemonsqueezyVariants.team',
    category: 'payments',
    englishLabel: 'Lemon Squeezy variant — Team',
    englishHelp: 'Lemon Squeezy variant id mapped to the Team plan tier.',
    keywords: ['payments', 'lemonsqueezy', 'variant', 'team', 'plan'],
  },
  {
    path: 'payments.lemonsqueezyVariants.enterprise',
    category: 'payments',
    englishLabel: 'Lemon Squeezy variant — Enterprise',
    englishHelp: 'Lemon Squeezy variant id mapped to the Enterprise plan tier.',
    keywords: ['payments', 'lemonsqueezy', 'variant', 'enterprise', 'plan'],
  },
  {
    path: 'payments.paddleVendorId',
    category: 'payments',
    englishLabel: 'Paddle vendor id',
    englishHelp: 'Vendor id used by Paddle Classic / Billing to identify the Rynora account.',
    keywords: ['payments', 'paddle', 'vendor', 'id'],
  },
  {
    path: 'payments.paddleApiKey',
    category: 'payments',
    englishLabel: 'Paddle API key',
    englishHelp: 'API key used to call the Paddle Billing API server-side. Sandbox or live, see `paddleEnvironment`.',
    keywords: ['payments', 'paddle', 'api', 'key'],
  },
  {
    path: 'payments.paddleEnvironment',
    category: 'payments',
    englishLabel: 'Paddle environment (sandbox / live)',
    englishHelp: 'Picks whether checkout + webhook traffic targets Paddle Sandbox or Production.',
    keywords: ['payments', 'paddle', 'environment', 'sandbox', 'live'],
  },
  {
    path: 'payments.paddleWebhookSecret',
    category: 'payments',
    englishLabel: 'Paddle webhook secret',
    englishHelp: 'Shared secret used to verify Paddle webhook signatures.',
    keywords: ['payments', 'paddle', 'webhook', 'secret'],
  },
  {
    path: 'payments.paddlePrices.starter',
    category: 'payments',
    englishLabel: 'Paddle price id — Starter',
    englishHelp: 'Paddle Billing price id mapped to the Starter plan tier.',
    keywords: ['payments', 'paddle', 'price', 'starter', 'plan'],
  },
  {
    path: 'payments.paddlePrices.pro',
    category: 'payments',
    englishLabel: 'Paddle price id — Pro',
    englishHelp: 'Paddle Billing price id mapped to the Pro plan tier.',
    keywords: ['payments', 'paddle', 'price', 'pro', 'plan'],
  },
  {
    path: 'payments.paddlePrices.team',
    category: 'payments',
    englishLabel: 'Paddle price id — Team',
    englishHelp: 'Paddle Billing price id mapped to the Team plan tier.',
    keywords: ['payments', 'paddle', 'price', 'team', 'plan'],
  },
  {
    path: 'payments.paddlePrices.enterprise',
    category: 'payments',
    englishLabel: 'Paddle price id — Enterprise',
    englishHelp: 'Paddle Billing price id mapped to the Enterprise plan tier.',
    keywords: ['payments', 'paddle', 'price', 'enterprise', 'plan'],
  },
  {
    path: 'payments.paypalClientId',
    category: 'payments',
    englishLabel: 'PayPal REST client id',
    englishHelp: 'Client id used to authenticate against PayPal REST. Public to the SPA so checkout buttons render.',
    keywords: ['payments', 'paypal', 'client', 'id'],
    relatedSettings: ['payments.paypalSecret', 'payments.paypalEnvironment'],
  },
  {
    path: 'payments.paypalSecret',
    category: 'payments',
    englishLabel: 'PayPal REST secret',
    englishHelp: 'Server-side secret used with `paypalClientId` to mint PayPal OAuth tokens.',
    keywords: ['payments', 'paypal', 'secret'],
  },
  {
    path: 'payments.paypalEnvironment',
    category: 'payments',
    englishLabel: 'PayPal environment (sandbox / live)',
    englishHelp: 'Picks whether PayPal checkout + webhooks target Sandbox or Production.',
    keywords: ['payments', 'paypal', 'environment', 'sandbox', 'live'],
  },
  {
    path: 'payments.paypalWebhookId',
    category: 'payments',
    englishLabel: 'PayPal webhook id',
    englishHelp: 'Webhook id used to verify incoming PayPal webhook signatures.',
    keywords: ['payments', 'paypal', 'webhook', 'id'],
  },
  {
    path: 'payments.paypalPlans.starter',
    category: 'payments',
    englishLabel: 'PayPal plan — Starter',
    englishHelp: 'PayPal Billing plan id mapped to the Starter plan tier.',
    keywords: ['payments', 'paypal', 'plan', 'starter'],
  },
  {
    path: 'payments.paypalPlans.pro',
    category: 'payments',
    englishLabel: 'PayPal plan — Pro',
    englishHelp: 'PayPal Billing plan id mapped to the Pro plan tier.',
    keywords: ['payments', 'paypal', 'plan', 'pro'],
  },
  {
    path: 'payments.paypalPlans.team',
    category: 'payments',
    englishLabel: 'PayPal plan — Team',
    englishHelp: 'PayPal Billing plan id mapped to the Team plan tier.',
    keywords: ['payments', 'paypal', 'plan', 'team'],
  },
  {
    path: 'payments.paypalPlans.enterprise',
    category: 'payments',
    englishLabel: 'PayPal plan — Enterprise',
    englishHelp: 'PayPal Billing plan id mapped to the Enterprise plan tier.',
    keywords: ['payments', 'paypal', 'plan', 'enterprise'],
  },
  {
    path: 'payments.razorpayKeyId',
    category: 'payments',
    englishLabel: 'Razorpay key id',
    englishHelp: 'Razorpay key id used to mint checkout sessions. Public to the SPA.',
    keywords: ['payments', 'razorpay', 'key', 'id', 'india'],
    relatedSettings: ['payments.razorpayKeySecret', 'payments.razorpayWebhookSecret'],
  },
  {
    path: 'payments.razorpayKeySecret',
    category: 'payments',
    englishLabel: 'Razorpay key secret',
    englishHelp: 'Server-side secret paired with `razorpayKeyId` for signed API calls.',
    keywords: ['payments', 'razorpay', 'key', 'secret'],
  },
  {
    path: 'payments.razorpayWebhookSecret',
    category: 'payments',
    englishLabel: 'Razorpay webhook secret',
    englishHelp: 'Shared secret used to verify HMAC signatures on incoming Razorpay webhooks.',
    keywords: ['payments', 'razorpay', 'webhook', 'secret', 'hmac'],
  },
  {
    path: 'payments.razorpayPlans.starter',
    category: 'payments',
    englishLabel: 'Razorpay plan — Starter',
    englishHelp: 'Razorpay subscription plan id mapped to the Starter plan tier.',
    keywords: ['payments', 'razorpay', 'plan', 'starter'],
  },
  {
    path: 'payments.razorpayPlans.pro',
    category: 'payments',
    englishLabel: 'Razorpay plan — Pro',
    englishHelp: 'Razorpay subscription plan id mapped to the Pro plan tier.',
    keywords: ['payments', 'razorpay', 'plan', 'pro'],
  },
  {
    path: 'payments.razorpayPlans.team',
    category: 'payments',
    englishLabel: 'Razorpay plan — Team',
    englishHelp: 'Razorpay subscription plan id mapped to the Team plan tier.',
    keywords: ['payments', 'razorpay', 'plan', 'team'],
  },
  {
    path: 'payments.razorpayPlans.enterprise',
    category: 'payments',
    englishLabel: 'Razorpay plan — Enterprise',
    englishHelp: 'Razorpay subscription plan id mapped to the Enterprise plan tier.',
    keywords: ['payments', 'razorpay', 'plan', 'enterprise'],
  },
  // ---- observability (v2 backfill: sentry + grafana + ai remediation + webhooks) ----
  {
    path: 'observability.sentryDsnFrontend',
    category: 'observability',
    englishLabel: 'Sentry DSN (frontend)',
    englishHelp: 'Sentry DSN forwarded to every SPA so browser errors land in Sentry. Public — DSNs are not secrets.',
    keywords: ['observability', 'sentry', 'dsn', 'frontend', 'spa', 'errors'],
    relatedSettings: ['observability.sentryEnvironment', 'observability.sentrySampleRate'],
  },
  {
    path: 'observability.sentryEnvironment',
    category: 'observability',
    englishLabel: 'Sentry environment tag',
    englishHelp:
      'Environment label attached to every Sentry event (e.g. `production`, `staging`). Helps filter in the dashboard.',
    keywords: ['observability', 'sentry', 'environment', 'tag'],
  },
  {
    path: 'observability.sentrySampleRate',
    category: 'observability',
    englishLabel: 'Sentry error sample rate (0–1)',
    englishHelp: 'Fraction of error events sent to Sentry. 1.0 = every event; 0.1 = 10%.',
    keywords: ['observability', 'sentry', 'sample', 'rate'],
  },
  {
    path: 'observability.sentryTracesSampleRate',
    category: 'observability',
    englishLabel: 'Sentry performance traces sample rate (0–1)',
    englishHelp: 'Fraction of performance transactions sent to Sentry. Keep low — traces are expensive.',
    keywords: ['observability', 'sentry', 'traces', 'sample', 'rate', 'performance'],
  },
  {
    path: 'observability.grafanaCloudUrl',
    category: 'observability',
    englishLabel: 'Grafana Cloud Mimir URL',
    englishHelp:
      'Remote-write URL used to push metrics into Grafana Cloud Mimir. Leave blank for self-hosted Prometheus.',
    keywords: ['observability', 'grafana', 'mimir', 'cloud', 'metrics', 'prometheus'],
    relatedSettings: ['observability.grafanaServiceAccountToken'],
  },
  {
    path: 'observability.grafanaServiceAccountToken',
    category: 'observability',
    englishLabel: 'Grafana Cloud service-account token',
    englishHelp: 'Bearer token used to authenticate metrics push to Grafana Cloud. Treat as a secret.',
    keywords: ['observability', 'grafana', 'cloud', 'token', 'secret'],
  },
  {
    path: 'observability.prometheusScrapeEnabled',
    category: 'observability',
    englishLabel: 'Expose Prometheus /metrics endpoint',
    englishHelp: 'When on, the API exposes `/api/metrics` so a Prometheus / Grafana Agent can scrape it.',
    keywords: ['observability', 'prometheus', 'metrics', 'scrape', 'endpoint'],
  },
  {
    path: 'observability.betterstackLogsToken',
    category: 'observability',
    englishLabel: 'Betterstack Logs token',
    englishHelp: 'Source token used to forward server logs to Better Stack (formerly Logtail).',
    keywords: ['observability', 'betterstack', 'logtail', 'logs', 'token'],
  },
  {
    path: 'observability.discordWebhookUrl',
    category: 'observability',
    englishLabel: 'Discord alert webhook (general)',
    englishHelp: 'Discord webhook used for general observability alerts (worker stuck, schedule drift).',
    keywords: ['observability', 'discord', 'webhook', 'alert'],
  },
  {
    path: 'observability.discordCriticalWebhookUrl',
    category: 'observability',
    englishLabel: 'Discord alert webhook (critical)',
    englishHelp: 'Separate Discord webhook for critical alerts so on-call sees them in a dedicated channel.',
    keywords: ['observability', 'discord', 'webhook', 'critical', 'oncall'],
  },
  {
    path: 'observability.aiRemediationEnabled',
    category: 'observability',
    englishLabel: 'Enable AI root-cause remediation',
    englishHelp:
      'When on, operators can request an LLM-backed remediation suggestion on an error fingerprint. Subject to per-org daily quota.',
    keywords: ['observability', 'ai', 'remediation', 'rca', 'fingerprint'],
    relatedSettings: ['observability.aiRemediationDailyQuotaPerOrg', 'ai.rcaProvider'],
  },
  {
    path: 'observability.aiRemediationDailyQuotaPerOrg',
    category: 'observability',
    englishLabel: 'AI remediation daily quota per org',
    englishHelp: 'Cap on the number of AI remediation calls per org per day. Protects against runaway LLM spend.',
    keywords: ['observability', 'ai', 'remediation', 'quota', 'daily', 'rate'],
  },
  {
    path: 'observability.firstSeenWebhookUrl',
    category: 'observability',
    englishLabel: 'First-seen error webhook URL',
    englishHelp:
      'When set, Rynora POSTs a versioned JSON payload to this URL the first time it sees a new error fingerprint.',
    keywords: ['observability', 'webhook', 'first-seen', 'fingerprint', 'alert'],
    relatedSettings: ['observability.firstSeenWebhookSecret'],
  },
  {
    path: 'observability.firstSeenWebhookSecret',
    category: 'observability',
    englishLabel: 'First-seen webhook signing secret',
    englishHelp:
      'HMAC-SHA256 secret used to sign first-seen webhook bodies. Receivers verify the `X-Rynora-Signature` header.',
    keywords: ['observability', 'webhook', 'first-seen', 'secret', 'hmac', 'signature'],
  },
  {
    path: 'observability.healNarrative',
    category: 'observability',
    englishLabel: 'Render auto-heal narrative on the run page',
    englishHelp:
      'When on, runs that succeeded via auto-heal display a narrative explaining which selector was rewritten.',
    keywords: ['observability', 'auto-heal', 'narrative', 'run', 'page', 'selector'],
  },
  {
    path: 'observability.weeklyDigestDow',
    category: 'observability',
    englishLabel: 'Weekly digest day of week',
    englishHelp: 'Day (0 = Sunday … 6 = Saturday) when the weekly observability digest dispatches.',
    keywords: ['observability', 'digest', 'weekly', 'day', 'schedule'],
  },
  {
    path: 'observability.weeklyDigestHourUtc',
    category: 'observability',
    englishLabel: 'Weekly digest hour (UTC)',
    englishHelp: 'Hour of day (0–23, UTC) when the weekly observability digest dispatches.',
    keywords: ['observability', 'digest', 'weekly', 'hour', 'utc', 'schedule'],
  },
  // ---- reporting (v2 backfill: branding + screenshots + share-link + retention) ----
  {
    path: 'reporting.branding.companyName',
    category: 'reporting',
    englishLabel: 'Report branding — company name',
    englishHelp: 'Company name printed on the report PDF header and the public share-link banner.',
    keywords: ['reporting', 'branding', 'company', 'name', 'pdf'],
  },
  {
    path: 'reporting.branding.reportName',
    category: 'reporting',
    englishLabel: 'Report branding — report name',
    englishHelp: 'Title prefix used in the report PDF (e.g. "QA Run Report"). Falls back to a default when blank.',
    keywords: ['reporting', 'branding', 'title', 'name'],
  },
  {
    path: 'reporting.branding.logoUrl',
    category: 'reporting',
    englishLabel: 'Report branding — logo URL',
    englishHelp: 'HTTPS URL of the logo image embedded in the report PDF header. Recommended SVG or 2× PNG.',
    keywords: ['reporting', 'branding', 'logo', 'pdf'],
  },
  {
    path: 'reporting.branding.primaryColor',
    category: 'reporting',
    englishLabel: 'Report branding — primary colour',
    englishHelp: 'Hex colour applied to report PDF headings and section dividers.',
    keywords: ['reporting', 'branding', 'colour', 'color', 'primary'],
  },
  {
    path: 'reporting.branding.accentColor',
    category: 'reporting',
    englishLabel: 'Report branding — accent colour',
    englishHelp: 'Hex colour used for highlights, badges, and pass/fail callouts in the report PDF.',
    keywords: ['reporting', 'branding', 'colour', 'color', 'accent'],
  },
  {
    path: 'reporting.screenshots.captureOnPass',
    category: 'reporting',
    englishLabel: 'Capture screenshots on passing steps',
    englishHelp: 'When on, the runner saves a screenshot for every passing step. Costs disk + uploads bandwidth.',
    keywords: ['reporting', 'screenshot', 'pass', 'capture', 'storage'],
  },
  {
    path: 'reporting.screenshots.captureOnFailure',
    category: 'reporting',
    englishLabel: 'Capture screenshots on failing steps',
    englishHelp: 'When on, the runner saves a screenshot for every failing step. Recommended ON for debugging.',
    keywords: ['reporting', 'screenshot', 'failure', 'capture', 'debug'],
  },
  {
    path: 'reporting.screenshots.format',
    category: 'reporting',
    englishLabel: 'Screenshot format (png / jpeg / webp)',
    englishHelp: 'Image format used when saving screenshots. PNG is lossless; JPEG / WebP are smaller.',
    keywords: ['reporting', 'screenshot', 'format', 'png', 'jpeg', 'webp'],
  },
  {
    path: 'reporting.screenshots.quality',
    category: 'reporting',
    englishLabel: 'Screenshot JPEG / WebP quality (1–100)',
    englishHelp: 'Quality level for lossy screenshot formats. Lower numbers save space but blur fine UI detail.',
    keywords: ['reporting', 'screenshot', 'quality', 'jpeg', 'webp', 'compression'],
  },
  {
    path: 'reporting.emailNotification.trigger',
    category: 'reporting',
    englishLabel: 'Email report — trigger',
    englishHelp: 'When to dispatch the report email (e.g. `on-fail`, `every-run`, `daily-digest`).',
    keywords: ['reporting', 'email', 'notification', 'trigger', 'when'],
  },
  {
    path: 'reporting.emailNotification.recipients',
    category: 'reporting',
    englishLabel: 'Email report — recipient list',
    englishHelp: 'Comma-separated email addresses that receive the report. Falls back to the org-owner when blank.',
    keywords: ['reporting', 'email', 'notification', 'recipients', 'to'],
  },
  {
    path: 'reporting.emailNotification.attachReport',
    category: 'reporting',
    englishLabel: 'Email report — attach PDF',
    englishHelp: 'When on, the report PDF is attached to the email. When off, only a link is sent.',
    keywords: ['reporting', 'email', 'notification', 'attach', 'pdf'],
  },
  {
    path: 'reporting.sections',
    category: 'reporting',
    englishLabel: 'Report sections to include',
    englishHelp:
      'Ordered list of sections the report renders (summary, run list, screenshots, accessibility, performance).',
    keywords: ['reporting', 'sections', 'pdf', 'layout', 'order'],
  },
  {
    path: 'reporting.retention.maxReportsPerFlow',
    category: 'reporting',
    englishLabel: 'Reports retention — max per flow',
    englishHelp: 'How many recent reports to keep per flow. Older reports are purged by the nightly retention sweep.',
    keywords: ['reporting', 'retention', 'storage', 'max', 'flow'],
  },
  {
    path: 'reporting.reportShareLinkDefaultExpiryHours',
    category: 'reporting',
    englishLabel: 'Share link — default expiry (hours)',
    englishHelp: 'Default lifetime of a freshly-minted report share link. Operators can shorten on a per-link basis.',
    keywords: ['reporting', 'share', 'link', 'expiry', 'ttl'],
  },
  {
    path: 'reporting.reportShareLinkDefaultRateLimitPerMin',
    category: 'reporting',
    englishLabel: 'Share link — default rate-limit (req/min)',
    englishHelp: 'Default per-token sliding-window rate limit on public report share links. Protects against scraping.',
    keywords: ['reporting', 'share', 'link', 'rate', 'limit'],
  },
  // ---- theme (v2 backfill: per-app + per-mode palette keys) ----
  {
    path: 'theme.light.accent',
    category: 'theme',
    englishLabel: 'Light theme — accent colour',
    englishHelp: 'Hex accent used for buttons and primary CTAs across every app in light mode.',
    keywords: ['theme', 'light', 'accent', 'colour', 'color', 'brand'],
  },
  {
    path: 'theme.light.accentStrong',
    category: 'theme',
    englishLabel: 'Light theme — strong accent',
    englishHelp: 'Darker accent used for hovered / pressed states of primary CTAs in light mode.',
    keywords: ['theme', 'light', 'accent', 'strong', 'hover'],
  },
  {
    path: 'theme.light.accentLight',
    category: 'theme',
    englishLabel: 'Light theme — soft accent',
    englishHelp: 'Lighter accent used for subtle backgrounds (badges, callouts) in light mode.',
    keywords: ['theme', 'light', 'accent', 'soft', 'badge'],
  },
  {
    path: 'theme.light.accentGlow',
    category: 'theme',
    englishLabel: 'Light theme — accent glow',
    englishHelp: 'Subtle accent halo used behind hero elements and important callouts in light mode.',
    keywords: ['theme', 'light', 'accent', 'glow'],
  },
  {
    path: 'theme.light.gradientStart',
    category: 'theme',
    englishLabel: 'Light theme — gradient start',
    englishHelp: 'Top-left hex stop of the brand gradient used on the marketing surface in light mode.',
    keywords: ['theme', 'light', 'gradient', 'start', 'brand'],
  },
  {
    path: 'theme.light.gradientEnd',
    category: 'theme',
    englishLabel: 'Light theme — gradient end',
    englishHelp: 'Bottom-right hex stop of the brand gradient used on the marketing surface in light mode.',
    keywords: ['theme', 'light', 'gradient', 'end', 'brand'],
  },
  {
    path: 'theme.dark.accent',
    category: 'theme',
    englishLabel: 'Dark theme — accent colour',
    englishHelp: 'Hex accent used for buttons and primary CTAs across every app in dark mode.',
    keywords: ['theme', 'dark', 'accent', 'colour', 'color'],
  },
  {
    path: 'theme.dark.accentStrong',
    category: 'theme',
    englishLabel: 'Dark theme — strong accent',
    englishHelp: 'Darker accent used for hovered / pressed states of primary CTAs in dark mode.',
    keywords: ['theme', 'dark', 'accent', 'strong', 'hover'],
  },
  {
    path: 'theme.dark.accentLight',
    category: 'theme',
    englishLabel: 'Dark theme — soft accent',
    englishHelp: 'Lighter accent used for subtle backgrounds (badges, callouts) in dark mode.',
    keywords: ['theme', 'dark', 'accent', 'soft', 'badge'],
  },
  {
    path: 'theme.dark.accentGlow',
    category: 'theme',
    englishLabel: 'Dark theme — accent glow',
    englishHelp: 'Subtle accent halo used behind hero elements and important callouts in dark mode.',
    keywords: ['theme', 'dark', 'accent', 'glow'],
  },
  {
    path: 'theme.dark.gradientStart',
    category: 'theme',
    englishLabel: 'Dark theme — gradient start',
    englishHelp: 'Top-left hex stop of the brand gradient used on the marketing surface in dark mode.',
    keywords: ['theme', 'dark', 'gradient', 'start'],
  },
  {
    path: 'theme.dark.gradientEnd',
    category: 'theme',
    englishLabel: 'Dark theme — gradient end',
    englishHelp: 'Bottom-right hex stop of the brand gradient used on the marketing surface in dark mode.',
    keywords: ['theme', 'dark', 'gradient', 'end'],
  },
  {
    path: 'theme.pickedAt',
    category: 'theme',
    englishLabel: 'Theme last-picked timestamp',
    englishHelp: 'ISO timestamp of the last time the theme was changed in the picker. Used to bust SPA caches.',
    keywords: ['theme', 'picked', 'timestamp', 'cache'],
  },
  {
    path: 'theme.appsUpdatedAt',
    category: 'theme',
    englishLabel: 'Per-app theme last-updated timestamp',
    englishHelp:
      'ISO timestamp of the last per-app theme override change. Used to bust SPA caches independently of the global theme.',
    keywords: ['theme', 'apps', 'updated', 'timestamp', 'cache'],
  },
  // ---- theme apps (per-app override mode) ----
  {
    path: 'theme.apps.landing-page',
    category: 'theme',
    englishLabel: 'Landing page theme mode',
    englishHelp:
      'Default theme (`light` / `dark` / `system`) for the public landing page. Operator override beats the visitor system preference.',
    keywords: ['theme', 'landing', 'page', 'mode', 'app'],
  },
  {
    path: 'theme.apps.flow-builder',
    category: 'theme',
    englishLabel: 'Flow builder theme mode',
    englishHelp:
      'Default theme (`light` / `dark` / `system`) for the in-product flow builder. Per-user picker still wins when allowed.',
    keywords: ['theme', 'flow-builder', 'editor', 'mode', 'app'],
  },
  {
    path: 'theme.apps.admin-console',
    category: 'theme',
    englishLabel: 'Admin console theme mode',
    englishHelp: 'Default theme (`light` / `dark` / `system`) for the admin console SPA.',
    keywords: ['theme', 'admin', 'console', 'mode', 'app'],
  },
  {
    path: 'theme.apps.admin-logs',
    category: 'theme',
    englishLabel: 'Admin logs theme mode',
    englishHelp: 'Default theme (`light` / `dark` / `system`) for the admin logs viewer SPA.',
    keywords: ['theme', 'admin', 'logs', 'mode', 'app'],
  },
  {
    path: 'theme.apps.admin-settings',
    category: 'theme',
    englishLabel: 'Admin settings theme mode',
    englishHelp: 'Default theme (`light` / `dark` / `system`) for the admin settings SPA.',
    keywords: ['theme', 'admin', 'settings', 'mode', 'app'],
  },
  // ---- access ----
  {
    path: 'access.allowedEmails.local',
    category: 'access',
    englishLabel: 'Allowed-email patterns — local',
    englishHelp:
      'Email patterns (exact or `*@domain.com`) that may sign in to the **local** environment. Empty = no restriction.',
    keywords: ['access', 'allowlist', 'email', 'local', 'env', 'environment'],
  },
  {
    path: 'access.allowedEmails.qa',
    category: 'access',
    englishLabel: 'Allowed-email patterns — QA',
    englishHelp:
      'Email patterns that may sign in to the **QA** environment. Tighten before sharing QA URLs with customers.',
    keywords: ['access', 'allowlist', 'email', 'qa', 'env', 'environment'],
  },
  {
    path: 'access.allowedEmails.uat',
    category: 'access',
    englishLabel: 'Allowed-email patterns — UAT',
    englishHelp: 'Email patterns that may sign in to the **UAT** environment. Typically the customer pilot cohort.',
    keywords: ['access', 'allowlist', 'email', 'uat', 'pilot', 'env'],
  },
  {
    path: 'access.allowedEmails.production',
    category: 'access',
    englishLabel: 'Allowed-email patterns — production',
    englishHelp:
      'Email patterns that may sign in to the **production** environment. Empty disables the allowlist (every signup is accepted).',
    keywords: ['access', 'allowlist', 'email', 'prod', 'production', 'env'],
  },
  {
    path: 'access.notifyOnReject',
    category: 'access',
    englishLabel: 'Notify on rejected sign-in',
    englishHelp:
      'When on, a rejected sign-in attempt (allowlist miss) fires an audit-log row + an operator notification.',
    keywords: ['access', 'allowlist', 'reject', 'notify', 'audit'],
  },
  // ---- ai (per-feature provider routing) ----
  {
    path: 'ai.anthropicBaseUrl',
    category: 'ai',
    englishLabel: 'Anthropic API base URL',
    englishHelp:
      'Override for the Anthropic Claude API base URL. Leave blank for the public endpoint; set this for self-hosted proxies.',
    keywords: ['ai', 'anthropic', 'claude', 'base', 'url', 'endpoint'],
  },
  {
    path: 'ai.intentProvider',
    category: 'ai',
    englishLabel: 'AI provider — intent parsing',
    englishHelp:
      'Which LLM provider handles natural-language intent parsing. Falls back to the org BYOK then platform key when blank.',
    keywords: ['ai', 'intent', 'provider', 'parse', 'routing'],
  },
  {
    path: 'ai.healingProvider',
    category: 'ai',
    englishLabel: 'AI provider — self-healing selectors',
    englishHelp:
      'Which LLM provider rewrites stale selectors. Pin a cheap latency-friendly provider here without affecting other AI surfaces.',
    keywords: ['ai', 'heal', 'healing', 'provider', 'selector', 'auto-heal'],
  },
  {
    path: 'ai.explainProvider',
    category: 'ai',
    englishLabel: 'AI provider — step explanations',
    englishHelp: 'Which LLM provider generates plain-English explanations on the run-detail page.',
    keywords: ['ai', 'explain', 'provider', 'narrative'],
  },
  {
    path: 'ai.debugProvider',
    category: 'ai',
    englishLabel: 'AI provider — conversational debug chat',
    englishHelp:
      'Which LLM provider backs the conversational debug panel. Often a different model from intent (chattier).',
    keywords: ['ai', 'debug', 'provider', 'chat', 'conversational'],
  },
  {
    path: 'ai.suggestActionsProvider',
    category: 'ai',
    englishLabel: 'AI provider — suggest-actions polish',
    englishHelp: 'Which LLM provider polishes smart-action suggestions when AI polish is enabled.',
    keywords: ['ai', 'suggest', 'actions', 'provider', 'smart-chips'],
  },
  {
    path: 'ai.rcaProvider',
    category: 'ai',
    englishLabel: 'AI provider — root-cause analysis',
    englishHelp:
      'Which LLM provider answers error-fingerprint remediation requests. Cheaper models are fine — RCA is bursty.',
    keywords: ['ai', 'rca', 'provider', 'root-cause', 'remediation', 'observability'],
  },
  {
    path: 'ai.a11yFixProvider',
    category: 'ai',
    englishLabel: 'AI provider — accessibility-fix suggestions',
    englishHelp: 'Which LLM provider rewrites failing contrast / a11y violations into suggested fixes.',
    keywords: ['ai', 'a11y', 'accessibility', 'fix', 'provider', 'contrast'],
  },
  {
    path: 'ai.tagSuggestEnabled',
    category: 'ai',
    englishLabel: 'Enable AI tag-suggest on flows',
    englishHelp:
      'When on, an "AI suggest tags" chip renders on flow cards without tags. Suggestions use a deterministic heuristic by default plus an LLM-rerank seam.',
    keywords: ['ai', 'tag', 'suggest', 'enabled', 'flows'],
  },
  {
    path: 'ai.tagSuggestProvider',
    category: 'ai',
    englishLabel: 'AI provider — tag suggestions',
    englishHelp: 'Which LLM provider reranks heuristic tag suggestions. Leave blank to use deterministic ranking only.',
    keywords: ['ai', 'tag', 'suggest', 'provider', 'rerank'],
  },
  {
    path: 'ai.qualityRiskProvider',
    category: 'ai',
    englishLabel: 'AI provider — business-risk proposals from stories',
    englishHelp:
      'Which LLM provider turns an ingested story into the business risks it puts at stake (the leading artefact on Story → Scenarios). Leave blank to inherit the default intent provider. Orgs with the AI opt-out never reach this call.',
    keywords: ['ai', 'quality', 'risk', 'story', 'provider', 'hq'],
  },
  {
    path: 'ai.scriptAuthorProvider',
    category: 'ai',
    englishLabel: 'AI provider — script generator',
    englishHelp:
      'Which LLM provider serves the "Generate with AI" button on the Custom Script step (POST /api/scripts/generate). Leave blank to inherit the default intent provider. Recommended pin is openai (gpt-4o-mini) for production code quality, or groq (llama-3.3-70b-versatile) for the cheap-but-good v1 path.',
    keywords: ['ai', 'script', 'author', 'provider', 'generate', 'code'],
  },
  {
    path: 'ai.toolUseEnabled',
    category: 'ai',
    englishLabel: 'Enable LLM function/tool calling',
    englishHelp:
      'When on, the `POST /api/intent/tool-call` surface accepts tool definitions and routes them to OpenAI / Groq / Anthropic.',
    keywords: ['ai', 'tool', 'function', 'calling', 'enabled'],
    relatedSettings: ['ai.toolUseProvider'],
  },
  {
    path: 'ai.toolUseProvider',
    category: 'ai',
    englishLabel: 'AI provider — tool/function calling',
    englishHelp:
      'Which LLM provider answers tool-calling requests. OpenAI and Anthropic have the strongest schemas; Groq is cheapest.',
    keywords: ['ai', 'tool', 'function', 'provider'],
  },
  {
    path: 'ai.visionProvider',
    category: 'ai',
    englishLabel: 'AI provider — screenshot vision',
    englishHelp: 'Which LLM provider answers screenshot-extraction requests via `POST /api/intent/vision`.',
    keywords: ['ai', 'vision', 'screenshot', 'provider', 'multimodal'],
  },
  // ---- auth (additional) ----
  {
    path: 'auth.githubClientId',
    category: 'auth',
    englishLabel: 'GitHub OAuth client id',
    englishHelp: 'Application id for GitHub sign-in. Configure the matching secret under `auth.githubClientSecret`.',
    keywords: ['auth', 'github', 'oauth', 'sso', 'login', 'client'],
    relatedSettings: ['auth.githubClientSecret'],
  },
  {
    path: 'auth.githubClientSecret',
    category: 'auth',
    englishLabel: 'GitHub OAuth client secret',
    englishHelp: 'Application secret for GitHub sign-in. Rotate carefully — rotating invalidates existing sign-ins.',
    keywords: ['auth', 'github', 'oauth', 'secret'],
  },
  {
    path: 'auth.appUrl',
    category: 'auth',
    englishLabel: 'Public app URL',
    englishHelp:
      'Canonical HTTPS URL for the flow-builder SPA. Used in OAuth callbacks, magic-link emails, and webhook bodies.',
    keywords: ['auth', 'url', 'app', 'callback', 'public'],
  },
  {
    path: 'auth.resetTokenExpiresHours',
    category: 'auth',
    englishLabel: 'Password-reset token lifetime (hours)',
    englishHelp: 'How long a password-reset link remains usable. Recommended ≤ 24 hours.',
    keywords: ['auth', 'reset', 'token', 'expiry', 'password'],
  },
  {
    path: 'auth.ipHashSalt',
    category: 'auth',
    englishLabel: 'IP-hash salt',
    englishHelp:
      'Server-side salt used to hash client IPs before they land in audit logs. Rotate to break correlation across windows.',
    keywords: ['auth', 'ip', 'hash', 'salt', 'audit', 'privacy'],
  },
  // ---- billing (Stripe legacy slice — separate from `payments`) ----
  {
    path: 'billing.stripePublishableKey',
    category: 'billing',
    englishLabel: 'Stripe publishable key',
    englishHelp: 'Stripe publishable key shipped to the SPA. Public — does not authorise server-side charges.',
    keywords: ['billing', 'stripe', 'publishable', 'key'],
  },
  {
    path: 'billing.encryptionKey',
    category: 'billing',
    englishLabel: 'Billing payload encryption key',
    englishHelp:
      'Symmetric key used to encrypt sensitive billing payloads (tax id, address) at rest. Rotating requires a re-encrypt sweep.',
    keywords: ['billing', 'encryption', 'key', 'secret'],
  },
  {
    path: 'billing.stripePrices.starter',
    category: 'billing',
    englishLabel: 'Stripe price id — Starter',
    englishHelp: 'Stripe price id mapped to the Starter plan tier.',
    keywords: ['billing', 'stripe', 'price', 'starter', 'plan'],
  },
  {
    path: 'billing.stripePrices.pro',
    category: 'billing',
    englishLabel: 'Stripe price id — Pro',
    englishHelp: 'Stripe price id mapped to the Pro plan tier.',
    keywords: ['billing', 'stripe', 'price', 'pro', 'plan'],
  },
  {
    path: 'billing.stripePrices.team',
    category: 'billing',
    englishLabel: 'Stripe price id — Team',
    englishHelp: 'Stripe price id mapped to the Team plan tier.',
    keywords: ['billing', 'stripe', 'price', 'team', 'plan'],
  },
  {
    path: 'billing.stripePrices.enterprise',
    category: 'billing',
    englishLabel: 'Stripe price id — Enterprise',
    englishHelp: 'Stripe price id mapped to the Enterprise plan tier.',
    keywords: ['billing', 'stripe', 'price', 'enterprise', 'plan'],
  },
  // ---- cdn (WAF rule slots) ----
  {
    path: 'cdn.wafRules.blockedCountryCodes',
    category: 'cdn',
    englishLabel: 'WAF — blocked country codes',
    englishHelp: 'ISO-3166 alpha-2 codes blocked at the CDN edge before traffic reaches the API. Empty = no geo-block.',
    keywords: ['cdn', 'waf', 'geoblock', 'country', 'security'],
  },
  {
    path: 'cdn.wafRules.rateLimitRegister',
    category: 'cdn',
    englishLabel: 'WAF — registration rate-limit',
    englishHelp: 'Per-IP rate budget for `POST /api/auth/register` enforced at the CDN edge.',
    keywords: ['cdn', 'waf', 'rate', 'limit', 'register', 'signup'],
  },
  {
    path: 'cdn.wafRules.botChallengeContact',
    category: 'cdn',
    englishLabel: 'WAF — contact form bot challenge',
    englishHelp: 'When on, the CDN issues a managed challenge (CAPTCHA / JS-challenge) on contact-form POSTs.',
    keywords: ['cdn', 'waf', 'bot', 'challenge', 'contact', 'captcha'],
  },
  // ---- cloudBrowser ----
  {
    path: 'cloudBrowser.defaultMode',
    category: 'cloudBrowser',
    englishLabel: 'Default cloud-browser mode',
    englishHelp:
      "Which provider runs flows when the operator doesn't pick one explicitly (`local` / `steel` / `saucelabs` / `browserstack` / `lambdatest`).",
    keywords: ['cloud', 'browser', 'mode', 'default', 'provider'],
  },
  {
    path: 'cloudBrowser.maxSessionMinutes',
    category: 'cloudBrowser',
    englishLabel: 'Max single-session length (minutes)',
    englishHelp:
      'Hard cap on how long one cloud-browser session can run before the reaper kills it. Protects against runaway charges.',
    keywords: ['cloud', 'browser', 'session', 'max', 'minutes', 'reaper'],
  },
  {
    path: 'cloudBrowser.idleSessionSeconds',
    category: 'cloudBrowser',
    englishLabel: 'Idle session timeout (seconds)',
    englishHelp:
      'How long a session can sit idle before the reaper closes it. Lower numbers save spend but kill flaky long-pause flows.',
    keywords: ['cloud', 'browser', 'idle', 'timeout', 'reaper'],
  },
  {
    path: 'cloudBrowser.reaperIntervalSeconds',
    category: 'cloudBrowser',
    englishLabel: 'Reaper poll interval (seconds)',
    englishHelp: 'How often the reaper worker scans for stale cloud-browser sessions to terminate.',
    keywords: ['cloud', 'browser', 'reaper', 'interval', 'worker'],
  },
  {
    path: 'cloudBrowser.overageHardCapMinutesPerOrg',
    category: 'cloudBrowser',
    englishLabel: 'Hard cap on cloud-browser minutes per org per month',
    englishHelp: 'Once an org exceeds this many cloud-browser minutes in a billing month, new sessions are refused.',
    keywords: ['cloud', 'browser', 'cap', 'minutes', 'org', 'overage', 'spend'],
  },
  {
    path: 'cloudBrowser.steelEndpoint',
    category: 'cloudBrowser',
    englishLabel: 'Steel.dev API endpoint',
    englishHelp: 'Override URL for the Steel.dev cloud-browser API. Leave blank for the public endpoint.',
    keywords: ['cloud', 'browser', 'steel', 'endpoint', 'url'],
    relatedSettings: ['cloudBrowser.steelApiKey'],
  },
  {
    path: 'cloudBrowser.steelApiKey',
    category: 'cloudBrowser',
    englishLabel: 'Steel.dev API key',
    englishHelp: 'Bearer token used to mint Steel.dev cloud-browser sessions.',
    keywords: ['cloud', 'browser', 'steel', 'api', 'key'],
  },
  {
    path: 'cloudBrowser.saucelabsUsername',
    category: 'cloudBrowser',
    englishLabel: 'Sauce Labs username',
    englishHelp: 'Sauce Labs username paired with the access key to mint cloud-browser sessions.',
    keywords: ['cloud', 'browser', 'saucelabs', 'sauce', 'username'],
    relatedSettings: ['cloudBrowser.saucelabsAccessKey', 'cloudBrowser.saucelabsRegion'],
  },
  {
    path: 'cloudBrowser.saucelabsAccessKey',
    category: 'cloudBrowser',
    englishLabel: 'Sauce Labs access key',
    englishHelp: 'Sauce Labs access key paired with the username. Treat as a secret.',
    keywords: ['cloud', 'browser', 'saucelabs', 'sauce', 'access', 'key'],
  },
  {
    path: 'cloudBrowser.saucelabsRegion',
    category: 'cloudBrowser',
    englishLabel: 'Sauce Labs region',
    englishHelp:
      'Sauce Labs region (`us-west-1` / `eu-central-1` / `apac-southeast-1`). Picks the closest data centre.',
    keywords: ['cloud', 'browser', 'saucelabs', 'region', 'datacentre'],
  },
  {
    path: 'cloudBrowser.browserstackAccessKey',
    category: 'cloudBrowser',
    englishLabel: 'BrowserStack access key',
    englishHelp:
      'BrowserStack access key used to mint Automate sessions. Paired with the BrowserStack username (admin-config or env).',
    keywords: ['cloud', 'browser', 'browserstack', 'access', 'key'],
  },
  {
    path: 'cloudBrowser.lambdatestUsername',
    category: 'cloudBrowser',
    englishLabel: 'LambdaTest username',
    englishHelp: 'LambdaTest username paired with the access key.',
    keywords: ['cloud', 'browser', 'lambdatest', 'username'],
    relatedSettings: ['cloudBrowser.lambdatestAccessKey'],
  },
  {
    path: 'cloudBrowser.lambdatestAccessKey',
    category: 'cloudBrowser',
    englishLabel: 'LambdaTest access key',
    englishHelp: 'LambdaTest access key paired with the username. Treat as a secret.',
    keywords: ['cloud', 'browser', 'lambdatest', 'access', 'key'],
  },
  // ---- connection (SPA bootstrap) ----
  {
    path: 'connection.apiBaseUrl',
    category: 'connection',
    englishLabel: 'API base URL (SPA bootstrap)',
    englishHelp:
      'Where the SPA looks for the Rynora API. Wraps `VITE_API_URL`; never reads `import.meta.env` outside the bootstrap.',
    keywords: ['connection', 'api', 'base', 'url', 'spa', 'bootstrap'],
  },
  {
    path: 'connection.wsUrl',
    category: 'connection',
    englishLabel: 'WebSocket URL',
    englishHelp:
      'Where the SPA opens the runner WebSocket connection. Usually the same host as `apiBaseUrl` but with `wss://`.',
    keywords: ['connection', 'websocket', 'ws', 'url', 'runner'],
  },
  {
    path: 'connection.wsReconnectMaxDelayMs',
    category: 'connection',
    englishLabel: 'WebSocket reconnect max delay (ms)',
    englishHelp:
      'Cap on exponential-backoff between WebSocket reconnect attempts. Lower numbers recover faster, higher numbers reduce flap-storms.',
    keywords: ['connection', 'websocket', 'reconnect', 'backoff', 'delay'],
  },
  {
    path: 'connection.healthCheckTimeoutMs',
    category: 'connection',
    englishLabel: 'Health-check timeout (ms)',
    englishHelp: 'How long the SPA waits on `/api/health` during bootstrap before showing a connection-error screen.',
    keywords: ['connection', 'health', 'check', 'timeout', 'bootstrap'],
  },
  // ---- contractTesting ----
  {
    path: 'contractTesting.gitToken',
    category: 'integrations',
    englishLabel: 'Git token for Pact fetch',
    englishHelp:
      'Bearer token used by the Mode B Pact-verify fetcher when pulling pacts from `github://` or `gitlab://` URLs.',
    keywords: ['contract', 'testing', 'pact', 'git', 'github', 'gitlab', 'token'],
  },
  // ---- defaultEnvironments (array leaf) ----
  {
    path: 'defaultEnvironments',
    category: 'connection',
    englishLabel: 'Default environment list',
    englishHelp:
      'Curated `local` / `qa` / `uat` / `production` environment definitions seeded onto new projects. Edit as JSON.',
    keywords: ['environments', 'default', 'env', 'list', 'project'],
  },
  // ---- editor ----
  {
    path: 'editor.autosaveDebounceMs',
    category: 'editor',
    englishLabel: 'Editor autosave debounce (ms)',
    englishHelp:
      'Idle time the editor waits before persisting an in-progress edit. Lower = more frequent saves, higher = fewer revisions.',
    keywords: ['editor', 'autosave', 'debounce', 'idle'],
  },
  {
    path: 'editor.promptOnUnsavedNavigation',
    category: 'editor',
    englishLabel: 'Prompt on unsaved navigation',
    englishHelp:
      'When on, the editor shows a "you have unsaved changes" prompt before navigating away. Recommended ON.',
    keywords: ['editor', 'unsaved', 'navigation', 'prompt', 'warn'],
  },
  // ---- email (additional) ----
  {
    path: 'email.contactEmail',
    category: 'email',
    englishLabel: 'Contact-form destination email',
    englishHelp: 'Where the public landing-page contact form posts. Leave blank to disable the form.',
    keywords: ['email', 'contact', 'form', 'destination'],
  },
  {
    path: 'email.smtpPass',
    category: 'email',
    englishLabel: 'SMTP password',
    englishHelp: 'Password for the SMTP server when `email.provider` is set to SMTP. Treat as a secret.',
    keywords: ['email', 'smtp', 'password', 'secret'],
  },
  // ---- flowTemplates ----
  {
    path: 'flowTemplates.enabled',
    category: 'noCode',
    englishLabel: 'Enable user-saved flow templates',
    englishHelp:
      'Master switch for the user-saved + AI-generated templates feature. When off, only the curated registry is exposed.',
    keywords: ['flow', 'template', 'enabled', 'killswitch', 'no-code'],
    relatedSettings: ['flowTemplates.maxPerOrg', 'flowTemplates.generatorEnabled'],
  },
  {
    path: 'flowTemplates.generatorEnabled',
    category: 'noCode',
    englishLabel: 'Enable AI template generation',
    englishHelp:
      'When on, operators can ask the LLM to draft a new flow template from a prompt. Subject to provider routing via `flowTemplates.generatorProvider`.',
    keywords: ['flow', 'template', 'ai', 'generator', 'enabled'],
  },
  {
    path: 'flowTemplates.generatorProvider',
    category: 'noCode',
    englishLabel: 'AI provider — flow-template generator',
    englishHelp:
      'Which LLM provider answers template-generation requests. Leave blank to fall through to the default AI router.',
    keywords: ['flow', 'template', 'ai', 'generator', 'provider'],
  },
  {
    path: 'flowTemplates.maxPerOrg',
    category: 'noCode',
    englishLabel: 'Max flow templates per org',
    englishHelp:
      'Cap on how many user-saved flow templates an org can persist. Returns `FLOW_TEMPLATE_LIMIT_REACHED` when exceeded.',
    keywords: ['flow', 'template', 'max', 'limit', 'org'],
  },
  // ---- integrations (GitHub App + Jira OAuth) ----
  {
    path: 'integrations.githubAppSlug',
    category: 'integrations',
    englishLabel: 'GitHub App slug',
    englishHelp: 'Slug of the Rynora GitHub App used for PR-comment + check-run integrations.',
    keywords: ['integrations', 'github', 'app', 'slug', 'pr'],
    relatedSettings: ['integrations.githubAppPrivateKey', 'integrations.githubAppWebhookSecret'],
  },
  {
    path: 'integrations.githubAppPrivateKey',
    category: 'integrations',
    englishLabel: 'GitHub App private key',
    englishHelp:
      'PEM-encoded private key used to sign GitHub App JWTs. Treat as a secret; rotating revokes outstanding installations.',
    keywords: ['integrations', 'github', 'app', 'private', 'key', 'secret'],
  },
  {
    path: 'integrations.githubAppWebhookSecret',
    category: 'integrations',
    englishLabel: 'GitHub App webhook secret',
    englishHelp: 'Shared secret used to verify HMAC signatures on incoming GitHub App webhooks.',
    keywords: ['integrations', 'github', 'app', 'webhook', 'secret', 'hmac'],
  },
  {
    path: 'integrations.jiraClientId',
    category: 'integrations',
    englishLabel: 'Jira OAuth client id',
    englishHelp:
      'Atlassian-issued client id for the Jira OAuth 2.0 (3LO) dance. Configure the secret + redirect URI to match.',
    keywords: ['integrations', 'jira', 'oauth', 'client', 'id'],
    relatedSettings: ['integrations.jiraClientSecret', 'integrations.jiraRedirectUri'],
  },
  {
    path: 'integrations.jiraClientSecret',
    category: 'integrations',
    englishLabel: 'Jira OAuth client secret',
    englishHelp: 'Atlassian-issued client secret for the Jira OAuth 2.0 (3LO) dance. Treat as a secret.',
    keywords: ['integrations', 'jira', 'oauth', 'client', 'secret'],
  },
  {
    path: 'integrations.jiraRedirectUri',
    category: 'integrations',
    englishLabel: 'Jira OAuth redirect URI',
    englishHelp:
      'Where Atlassian sends the user back after the OAuth dance. Must exactly match what is registered with Atlassian.',
    keywords: ['integrations', 'jira', 'oauth', 'redirect', 'uri', 'callback'],
  },
  {
    path: 'integrations.jiraWebhookSecret',
    category: 'integrations',
    englishLabel: 'Jira webhook secret',
    englishHelp: 'Shared secret used to verify HMAC signatures on incoming Jira webhooks (issue state changes).',
    keywords: ['integrations', 'jira', 'webhook', 'secret', 'hmac'],
  },
  // ---- monitors (defaults the v1.1 admin tab edits) ----
  {
    path: 'monitors.defaultChannelTargets.pagerdutyIntegrationKey',
    category: 'monitors',
    englishLabel: 'Default PagerDuty integration key',
    englishHelp: 'Org-wide default PagerDuty Events v2 key. Per-monitor channel targets win over this when set.',
    keywords: ['monitors', 'pagerduty', 'integration', 'key', 'default'],
  },
  {
    path: 'monitors.defaultChannelTargets.slackWebhookUrl',
    category: 'monitors',
    englishLabel: 'Default Slack webhook URL',
    englishHelp: 'Org-wide default Slack incoming-webhook URL. Must start with `https://hooks.slack.com/`.',
    keywords: ['monitors', 'slack', 'webhook', 'url', 'default'],
  },
  {
    path: 'monitors.defaultChannelTargets.webhookUrl',
    category: 'monitors',
    englishLabel: 'Default generic webhook URL',
    englishHelp: 'Org-wide default for the generic JSON webhook channel. Signed with `webhookSecret` when set.',
    keywords: ['monitors', 'webhook', 'url', 'default', 'generic'],
    relatedSettings: ['monitors.defaultChannelTargets.webhookSecret'],
  },
  {
    path: 'monitors.defaultChannelTargets.webhookSecret',
    category: 'monitors',
    englishLabel: 'Default generic webhook signing secret',
    englishHelp:
      'HMAC-SHA256 secret used to sign generic webhook bodies. Receivers verify the `X-Rynora-Signature` header.',
    keywords: ['monitors', 'webhook', 'secret', 'hmac', 'signature'],
  },
  {
    path: 'monitors.defaultPageChannels',
    category: 'monitors',
    englishLabel: 'Default page-channel list',
    englishHelp:
      'Channels (e.g. `in_app`, `email`, `pagerduty`) that fire when a monitor pages and no per-monitor override exists.',
    keywords: ['monitors', 'page', 'channels', 'default'],
  },
  {
    path: 'monitors.retentionDays',
    category: 'monitors',
    englishLabel: 'Monitor status retention (days)',
    englishHelp: 'How long `monitor_status_events` are kept before the nightly retention sweep prunes them.',
    keywords: ['monitors', 'retention', 'days', 'cleanup'],
  },
  // ---- noCode ----
  {
    path: 'noCode.components.versionRetention',
    category: 'noCode',
    englishLabel: 'No-code component version retention',
    englishHelp: 'How many previous versions of a no-code component to keep before garbage collection.',
    keywords: ['no-code', 'component', 'version', 'retention'],
  },
  {
    path: 'noCode.describeMode.aiRefine.enabled',
    category: 'noCode',
    englishLabel: 'Enable AI refinement of Describe Mode output',
    englishHelp:
      'When on, Describe Mode pipes its draft through the LLM router for a polish pass. Subject to cache TTL below.',
    keywords: ['no-code', 'describe', 'ai', 'refine', 'enabled'],
    relatedSettings: ['noCode.describeMode.aiRefine.cacheTtlDays'],
  },
  {
    path: 'noCode.describeMode.aiRefine.cacheTtlDays',
    category: 'noCode',
    englishLabel: 'Describe Mode AI-refine cache TTL (days)',
    englishHelp: 'How long cached AI refinement results stay valid. 0 disables the cache write.',
    keywords: ['no-code', 'describe', 'ai', 'refine', 'cache', 'ttl'],
  },
  {
    path: 'noCode.describeMode.autoPromote.enabled',
    category: 'noCode',
    englishLabel: 'Auto-promote Describe Mode drafts',
    englishHelp:
      'When on, a high-confidence Describe Mode draft is auto-promoted to a real flow without a review step.',
    keywords: ['no-code', 'describe', 'auto', 'promote', 'enabled'],
  },
  {
    path: 'noCode.naturalLanguageAuthoring.cacheTtlDays',
    category: 'noCode',
    englishLabel: 'Natural-language authoring cache TTL (days)',
    englishHelp: 'How long the LLM step-draft cache stays valid for the natural-language authoring surface.',
    keywords: ['no-code', 'natural', 'language', 'cache', 'ttl'],
  },
  {
    path: 'noCode.suggestActions.enabled',
    category: 'noCode',
    englishLabel: 'Enable suggested-actions smart chips',
    englishHelp:
      'Master switch for the smart-chip strip in the flow editor. When off, only manual step insertion is exposed.',
    keywords: ['no-code', 'suggest', 'actions', 'smart', 'chips', 'enabled'],
  },
  {
    path: 'noCode.suggestActions.maxElements',
    category: 'noCode',
    englishLabel: 'Smart-chip max elements scanned',
    englishHelp:
      'Cap on the number of DOM elements the smart-chip scanner inspects. Higher = slower but catches more candidates.',
    keywords: ['no-code', 'suggest', 'actions', 'max', 'elements', 'dom'],
  },
  {
    path: 'noCode.suggestActions.globalPanel.enabled',
    category: 'noCode',
    englishLabel: 'Enable global smart-chip panel',
    englishHelp: 'When on, the smart-chip strip floats above the editor instead of inside the step picker.',
    keywords: ['no-code', 'suggest', 'actions', 'global', 'panel', 'enabled'],
  },
  {
    path: 'noCode.suggestActions.aiPolish.enabled',
    category: 'noCode',
    englishLabel: 'Enable AI polish on smart chips',
    englishHelp: 'When on, smart-chip labels are rewritten by the LLM router for friendlier copy.',
    keywords: ['no-code', 'suggest', 'actions', 'ai', 'polish', 'enabled'],
    relatedSettings: ['noCode.suggestActions.aiPolish.cacheTtlDays', 'ai.suggestActionsProvider'],
  },
  {
    path: 'noCode.suggestActions.aiPolish.cacheTtlDays',
    category: 'noCode',
    englishLabel: 'Smart-chip AI-polish cache TTL (days)',
    englishHelp: 'How long cached AI-polished labels stay valid before re-querying the LLM.',
    keywords: ['no-code', 'suggest', 'actions', 'ai', 'polish', 'cache', 'ttl'],
  },
  {
    path: 'noCode.suggestActions.serverSide.enabled',
    category: 'noCode',
    englishLabel: 'Enable server-side smart-action classifier',
    englishHelp:
      'When on, the SPA calls `POST /api/suggested-actions/classify` for per-project classification. Silently falls back to client-only on failure.',
    keywords: ['no-code', 'suggest', 'actions', 'server', 'side', 'classify'],
    relatedSettings: ['noCode.suggestActions.classifyCacheTtlDays'],
  },
  {
    path: 'noCode.suggestActions.classifyCacheTtlDays',
    category: 'noCode',
    englishLabel: 'Server-side classify cache TTL (days)',
    englishHelp:
      'Postgres cache TTL for the classify endpoint. 0 disables the cache write — the service still responds.',
    keywords: ['no-code', 'suggest', 'actions', 'classify', 'cache', 'ttl', 'server'],
  },
  {
    // Phase 11.4–11.5 — operator-facing canary control for the
    // suggestion panel layout. The schema is a Zod enum
    // (`'v1' | 'v2' | 'v3'`), which the admin-settings schema-form
    // renders automatically as a <SelectInput> dropdown. Default
    // stays `'v1'` (legacy flat strip) so the change is invisible
    // until an operator opts in per env; flipping to `'v3'` enables
    // the quick-picks + browse-all-modal layout on next editor
    // mount (recommended). `v2` remains available for rollback A/B
    // while v3 bakes.
    path: 'noCode.suggestActions.layoutVersion',
    category: 'noCode',
    englishLabel: 'Suggestion-panel layout (v1 / v2 / v3 — recommended: v3)',
    englishHelp:
      'v1 = legacy flat chip strip. v2 = grouped element rows under landmark headers (hits a wall on content-heavy pages). v3 (recommended) = top 5 Quick Picks + searchable Browse-all modal + rich VerifyAssertionPicker covering all 10 assertion kinds. Telemetry rides flow.suggest.shown for A/B. Flip per env; rollback is instant (no deploy).',
    keywords: [
      'no-code',
      'suggest',
      'actions',
      'layout',
      'version',
      'v1',
      'v2',
      'v3',
      'rows',
      'grouped',
      'element',
      'row',
      'panel',
      'quick',
      'picks',
      'browse',
      'modal',
      'phase 11.4',
      'phase 11.5',
      'canary',
    ],
    relatedSettings: ['noCode.suggestActions.enabled', 'noCode.suggestActions.maxElements'],
  },
  // ---- noCode.runJs (pending-runjs-step) ----
  {
    path: 'noCode.runJs.enabled',
    category: 'noCode',
    englishLabel: 'Enable the inline runJS() step',
    englishHelp:
      'Master switch for the browser-context code escape hatch. Defaults OFF on fresh installs — flip on after reviewing the security posture. Distinct from the existing server-side Script step.',
    keywords: ['no-code', 'runjs', 'run-js', 'browser', 'eval', 'escape', 'hatch', 'enabled', 'kill', 'switch'],
    relatedSettings: [
      'noCode.runJs.maxTimeoutMs',
      'noCode.runJs.defaultNetwork',
      'noCode.runJs.maxCodeBytes',
      'noCode.runJs.bannedPatternsLint',
    ],
  },
  {
    path: 'noCode.runJs.maxTimeoutMs',
    category: 'noCode',
    englishLabel: 'runJS step max timeout (ms)',
    englishHelp:
      'Hard ceiling per runJS step in milliseconds. The runner clamps each step to the smaller of this value and the per-step timeout field. Default 30000 ms (30 s); hard upper bound 60000 ms (60 s).',
    keywords: ['no-code', 'runjs', 'timeout', 'ms', 'limit', 'browser', 'eval'],
  },
  {
    path: 'noCode.runJs.defaultNetwork',
    category: 'noCode',
    englishLabel: 'runJS default network mode',
    englishHelp:
      "Org-default for the per-step network field. 'inherit' lets the script fetch() against the page origin under CSP (matches Cypress/Playwright). 'blocked' makes the runner abort every outbound request from the eval — coarse; allowlists are a v2 ask.",
    keywords: ['no-code', 'runjs', 'network', 'inherit', 'blocked', 'default', 'fetch', 'security'],
  },
  {
    path: 'noCode.runJs.maxCodeBytes',
    category: 'noCode',
    englishLabel: 'runJS max code body (bytes)',
    englishHelp:
      'Maximum size of one runJS step body in bytes. Default 32768 (32 KB); hard upper bound 131072 (128 KB). Catches "let me paste my entire bundled SPA into a step".',
    keywords: ['no-code', 'runjs', 'code', 'bytes', 'size', 'limit', 'body'],
  },
  {
    path: 'noCode.runJs.bannedPatternsLint',
    category: 'noCode',
    englishLabel: 'Lint runJS bodies for eval/new Function',
    englishHelp:
      'When on, the API refuses to save a runJS step whose body contains eval( / new Function( / Function( — coarse defence in front of the browser sandbox. The real defence is the sandbox; this signals organisational intent.',
    keywords: ['no-code', 'runjs', 'banned', 'pattern', 'lint', 'eval', 'function', 'security'],
  },
  // ---- ops (operational flags) ----
  {
    path: 'ops.drainTimeoutSec',
    category: 'ops',
    englishLabel: 'Drain timeout (seconds)',
    englishHelp: 'How long the API process waits for in-flight work to finish before SIGKILL on shutdown.',
    keywords: ['ops', 'drain', 'timeout', 'shutdown', 'lifecycle'],
  },
  {
    path: 'ops.flags.deletionWorkerEnabled',
    category: 'ops',
    englishLabel: 'Enable deletion worker',
    englishHelp: 'Master switch for the nightly deletion-sweep worker (purges expired runs / screenshots / traces).',
    keywords: ['ops', 'flag', 'deletion', 'worker', 'enabled'],
  },
  {
    path: 'ops.flags.exportWorkerEnabled',
    category: 'ops',
    englishLabel: 'Enable export worker',
    englishHelp: 'Master switch for the background export worker (PDF reports, share-link rebuilds).',
    keywords: ['ops', 'flag', 'export', 'worker', 'enabled'],
  },
  {
    path: 'ops.flags.stripeWebhookEnabled',
    category: 'ops',
    englishLabel: 'Enable Stripe webhook ingestion',
    englishHelp:
      'Master switch for the Stripe webhook receiver. Disable to stop billing-event ingestion during incident response.',
    keywords: ['ops', 'flag', 'stripe', 'webhook', 'enabled'],
  },
  {
    path: 'ops.flags.weeklyDigestEnabled',
    category: 'ops',
    englishLabel: 'Enable weekly digest worker',
    englishHelp: 'Master switch for the weekly-digest job. Disable to silence digest emails during incident response.',
    keywords: ['ops', 'flag', 'digest', 'weekly', 'enabled'],
  },
  // ---- perfTrends ----
  {
    path: 'perfTrends.enabled',
    category: 'observability',
    englishLabel: 'Enable perf-trend aggregation',
    englishHelp: 'Master switch for the daily perf-aggregator worker. When off, no perf-trend rows are written.',
    keywords: ['perf', 'trends', 'enabled', 'killswitch', 'worker'],
    relatedSettings: ['perfTrends.readEnabled', 'perfTrends.postgresMirrorEnabled'],
  },
  {
    path: 'perfTrends.readEnabled',
    category: 'observability',
    englishLabel: 'Enable perf-trend read API',
    englishHelp:
      'When on, `GET /api/projects/:projectId/perf/trend` is exposed. Disable to hide trends while debugging.',
    keywords: ['perf', 'trends', 'read', 'enabled', 'api'],
  },
  {
    path: 'perfTrends.postgresMirrorEnabled',
    category: 'observability',
    englishLabel: 'Mirror perf trends into Postgres',
    englishHelp: 'When on, the worker UPSERTs daily perf-trend rows into the `perf_trend_daily` Postgres table.',
    keywords: ['perf', 'trends', 'postgres', 'mirror', 'enabled'],
  },
  {
    path: 'perfTrends.sink',
    category: 'observability',
    englishLabel: 'Perf-trend sink',
    englishHelp:
      'Which external sink the worker pushes perf points to (`timescale` / `prometheus` / `grafanaCloud` / `victoriaMetrics` / `influxdb` / `datadog`).',
    keywords: ['perf', 'trends', 'sink', 'tsdb', 'prometheus', 'grafana', 'datadog'],
  },
  {
    path: 'perfTrends.retentionDays',
    category: 'observability',
    englishLabel: 'Perf-trend retention (days)',
    englishHelp: 'How long perf-trend rows are kept in Postgres before the nightly retention sweep prunes them.',
    keywords: ['perf', 'trends', 'retention', 'days', 'storage'],
  },
  {
    path: 'perfTrends.worker.runHourUtc',
    category: 'observability',
    englishLabel: 'Perf-aggregator run hour (UTC)',
    englishHelp:
      'Hour of day (0–23, UTC) when the perf-aggregator worker fires. Defaults to 05:00 UTC after flake + coverage.',
    keywords: ['perf', 'trends', 'worker', 'run', 'hour', 'utc'],
  },
  {
    path: 'perfTrends.worker.lookbackHours',
    category: 'observability',
    englishLabel: 'Perf-aggregator lookback (hours)',
    englishHelp:
      'How far back the worker re-scans `test_runs` on each tick. Higher reduces gaps after downtime; lower reduces CPU.',
    keywords: ['perf', 'trends', 'worker', 'lookback', 'hours'],
  },
  // ---- providerOAuth (surfaces under the Integrations tab) ----
  {
    path: 'providerOAuth.enabled',
    category: 'integrations',
    englishLabel: 'Enable provider-OAuth flow (Gmail / Outlook)',
    englishHelp: 'Master switch for the Connect-Gmail / Connect-Outlook flow used by email presets in the picker.',
    keywords: ['provider', 'oauth', 'enabled', 'gmail', 'outlook'],
  },
  {
    path: 'providerOAuth.redirectUrl',
    category: 'integrations',
    englishLabel: 'Provider-OAuth redirect URL',
    englishHelp:
      'Where Google / Microsoft send the user back after the OAuth dance. Must match what is registered with each provider.',
    keywords: ['provider', 'oauth', 'redirect', 'url', 'callback'],
  },
  {
    path: 'providerOAuth.gmailClientId',
    category: 'integrations',
    englishLabel: 'Gmail OAuth client id',
    englishHelp: 'Google-issued client id for the Gmail OAuth dance.',
    keywords: ['provider', 'oauth', 'gmail', 'google', 'client', 'id'],
    relatedSettings: ['providerOAuth.gmailClientSecret'],
  },
  {
    path: 'providerOAuth.gmailClientSecret',
    category: 'integrations',
    englishLabel: 'Gmail OAuth client secret',
    englishHelp: 'Google-issued client secret for the Gmail OAuth dance. Treat as a secret.',
    keywords: ['provider', 'oauth', 'gmail', 'google', 'client', 'secret'],
  },
  {
    path: 'providerOAuth.outlookClientId',
    category: 'integrations',
    englishLabel: 'Outlook OAuth client id',
    englishHelp: 'Microsoft-issued application id for the Outlook OAuth dance.',
    keywords: ['provider', 'oauth', 'outlook', 'microsoft', 'azure', 'client', 'id'],
    relatedSettings: ['providerOAuth.outlookClientSecret', 'providerOAuth.outlookTenant'],
  },
  {
    path: 'providerOAuth.outlookClientSecret',
    category: 'integrations',
    englishLabel: 'Outlook OAuth client secret',
    englishHelp: 'Microsoft-issued application secret for the Outlook OAuth dance.',
    keywords: ['provider', 'oauth', 'outlook', 'microsoft', 'secret'],
  },
  {
    path: 'providerOAuth.outlookTenant',
    category: 'integrations',
    englishLabel: 'Outlook tenant id',
    englishHelp:
      'Microsoft tenant id used in the authorise URL. Use `common` for any-account or `consumers` for personal MSAs.',
    keywords: ['provider', 'oauth', 'outlook', 'microsoft', 'tenant', 'azure'],
  },
  // ---- runner ----
  {
    path: 'runner.browserTier',
    category: 'runner',
    englishLabel: 'Runner browser tier',
    englishHelp:
      'Which browser tier the local runner ships with (`chromium` / `chromium-firefox-webkit`). Higher tiers download more deps on first launch.',
    keywords: ['runner', 'browser', 'tier', 'chromium', 'firefox', 'webkit'],
  },
  {
    path: 'runner.steelApiKey',
    category: 'runner',
    englishLabel: 'Steel.dev API key (legacy runner slot)',
    englishHelp:
      'Legacy runner slot for the Steel.dev API key. Prefer the equivalent under `cloudBrowser.steelApiKey`.',
    keywords: ['runner', 'steel', 'api', 'key', 'legacy'],
  },
  {
    path: 'runner.traceDir',
    category: 'runner',
    englishLabel: 'Trace zip directory',
    englishHelp: 'Server-side path where Playwright trace zips are written. Must be writable by the runner process.',
    keywords: ['runner', 'trace', 'directory', 'storage'],
  },
  {
    path: 'runner.traceRetentionDays',
    category: 'runner',
    englishLabel: 'Trace retention (days)',
    englishHelp: 'How long trace zips are kept before the nightly retention sweep prunes them.',
    keywords: ['runner', 'trace', 'retention', 'days'],
  },
  {
    path: 'runner.defaultViewport.width',
    category: 'runner',
    englishLabel: 'Default viewport width (px)',
    englishHelp: 'Fallback viewport width when a flow does not declare a `defaultViewportPresetId`. Recommended 1280.',
    keywords: ['runner', 'viewport', 'width', 'default'],
  },
  {
    path: 'runner.defaultViewport.height',
    category: 'runner',
    englishLabel: 'Default viewport height (px)',
    englishHelp: 'Fallback viewport height when a flow does not declare a `defaultViewportPresetId`. Recommended 800.',
    keywords: ['runner', 'viewport', 'height', 'default'],
  },
  {
    path: 'runner.wsAllowedOrigins',
    category: 'runner',
    englishLabel: 'Runner WebSocket allowed origins',
    englishHelp: 'Origins (comma-separated) allowed to open the runner WebSocket. Empty = no CORS check (dev only).',
    keywords: ['runner', 'websocket', 'origin', 'cors', 'allowlist'],
  },
  // ---- salesforce ----
  {
    path: 'salesforce.presetsEnabled',
    category: 'integrations',
    englishLabel: 'Enable Salesforce presets',
    englishHelp:
      'When on, the Salesforce SF0 preset catalogue is exposed via `GET /api/salesforce-presets`. Default OFF — beta surface.',
    keywords: ['salesforce', 'sfdc', 'presets', 'enabled'],
  },
  {
    path: 'salesforce.defaultInstanceUrl',
    category: 'integrations',
    englishLabel: 'Default Salesforce instance URL',
    englishHelp:
      'Default `https://yourorg.lightning.force.com` URL used by the SFDC presets when one is not provided per call.',
    keywords: ['salesforce', 'sfdc', 'instance', 'url', 'default'],
  },
  // ---- screenshotRedaction (surfaces under the Security tab) ----
  {
    path: 'screenshotRedaction.enabled',
    category: 'security',
    englishLabel: 'Enable screenshot redaction',
    englishHelp:
      'Master switch for screenshot redaction. When on, matching elements are blurred or blacked out before the screenshot is persisted.',
    keywords: ['screenshot', 'redaction', 'enabled', 'pii'],
  },
  {
    path: 'screenshotRedaction.useDefaultSelectors',
    category: 'security',
    englishLabel: 'Apply default redaction selectors',
    englishHelp:
      'When on, four curated default selectors (passwords / credit-card inputs / auth-token inputs / `[data-pii]`) are layered with operator additions.',
    keywords: ['screenshot', 'redaction', 'default', 'selectors', 'pii'],
  },
  {
    path: 'screenshotRedaction.selectors',
    category: 'security',
    englishLabel: 'Custom redaction selectors',
    englishHelp:
      'CSS selectors that match elements to redact in screenshots. Layered on top of the defaults when those are enabled.',
    keywords: ['screenshot', 'redaction', 'selectors', 'css', 'custom'],
  },
  {
    path: 'screenshotRedaction.redactionStyle',
    category: 'security',
    englishLabel: 'Redaction style',
    englishHelp: 'How matched elements are obscured: `blur` (Gaussian blur) or `blackout` (solid rectangle).',
    keywords: ['screenshot', 'redaction', 'style', 'blur', 'blackout'],
  },
  {
    path: 'screenshotRedaction.redactConsoleLogs',
    category: 'security',
    englishLabel: 'Redact PII in console logs',
    englishHelp:
      'When on, captured console logs run through 5 curated regex redactions (email / credit-card / JWT / phone / SSN) before persistence.',
    keywords: ['screenshot', 'redaction', 'console', 'logs', 'pii', 'regex'],
  },
  // ---- secretsManager ----
  {
    path: 'secretsManager.bitwardenServerUrl',
    category: 'secretsManager',
    englishLabel: 'Bitwarden server URL',
    englishHelp: 'Override URL for the Bitwarden Secrets Manager API. Leave blank for the public endpoint.',
    keywords: ['secrets', 'bitwarden', 'server', 'url'],
    relatedSettings: ['secretsManager.bitwardenAccessToken'],
  },
  {
    path: 'secretsManager.bitwardenAccessToken',
    category: 'secretsManager',
    englishLabel: 'Bitwarden access token',
    englishHelp: 'Bearer token used to authenticate against the Bitwarden Secrets Manager API.',
    keywords: ['secrets', 'bitwarden', 'access', 'token'],
  },
  // ---- security (DAST run gating) ----
  {
    path: 'security.dast.softMode',
    category: 'security',
    englishLabel: 'DAST soft mode',
    englishHelp:
      'When on, continuous DAST findings are logged but do not gate deployments. Useful while tuning the ruleset.',
    keywords: ['security', 'dast', 'soft', 'mode'],
  },
  {
    path: 'security.dast.failOnWarn',
    category: 'security',
    englishLabel: 'DAST — fail on warning',
    englishHelp:
      'When on, a `warn`-severity DAST finding fails the deploy gate. Recommended OFF during initial rollout.',
    keywords: ['security', 'dast', 'fail', 'warn'],
  },
  {
    path: 'security.dast.failOnFail',
    category: 'security',
    englishLabel: 'DAST — fail on fail',
    englishHelp: 'When on, a `fail`-severity DAST finding fails the deploy gate. Recommended ON.',
    keywords: ['security', 'dast', 'fail', 'gate'],
  },
  {
    path: 'security.dast.notifyUserId',
    category: 'security',
    englishLabel: 'DAST notification recipient user id',
    englishHelp:
      'User id that receives DAST-finding notifications. Falls back to the security-on-call user when blank.',
    keywords: ['security', 'dast', 'notify', 'user', 'oncall'],
  },
  // ---- server ----
  {
    path: 'server.apiPort',
    category: 'server',
    englishLabel: 'API listen port',
    englishHelp: 'TCP port the API process binds to. Read once at boot from the bootstrap config loader.',
    keywords: ['server', 'api', 'port', 'listen'],
  },
  {
    path: 'server.runnerPort',
    category: 'server',
    englishLabel: 'Runner listen port',
    englishHelp: 'TCP port the runner process binds to. Separate from the API port so they can scale independently.',
    keywords: ['server', 'runner', 'port', 'listen'],
  },
  {
    path: 'server.corsOrigin',
    category: 'server',
    englishLabel: 'CORS allowed origins',
    englishHelp: 'Comma-separated origins (or `*` for dev) accepted by the API CORS middleware.',
    keywords: ['server', 'cors', 'origin', 'security'],
  },
  {
    path: 'server.rateLimitWindowMs',
    category: 'server',
    englishLabel: 'Rate-limit window (ms)',
    englishHelp: 'Sliding-window length for the API rate limiter. 60_000 = per-minute budget.',
    keywords: ['server', 'rate', 'limit', 'window'],
    relatedSettings: ['server.rateLimitMax'],
  },
  {
    path: 'server.rateLimitMax',
    category: 'server',
    englishLabel: 'Rate-limit max requests per window',
    englishHelp: 'Hard cap on API requests per client IP per rate-limit window.',
    keywords: ['server', 'rate', 'limit', 'max'],
  },
  {
    path: 'server.screenshotDir',
    category: 'server',
    englishLabel: 'Screenshot directory',
    englishHelp: 'Server-side path where step-screenshot files are written. Must be writable by the API process.',
    keywords: ['server', 'screenshot', 'directory', 'storage'],
  },
  {
    path: 'server.jwtExpiry',
    category: 'server',
    englishLabel: 'JWT session expiry',
    englishHelp:
      'How long a fresh JWT remains valid. Recommended ≤ 24 hours; the CLI prefers long-lived API keys for CI/CD.',
    keywords: ['server', 'jwt', 'expiry', 'session'],
  },
  // ---- storage ----
  {
    path: 'storage.provider',
    category: 'storage',
    englishLabel: 'Artefact storage provider',
    englishHelp:
      'Where screenshot / trace / report artefacts are persisted (`local-disk` / `s3` / `b2`). Operator picks one.',
    keywords: ['storage', 'provider', 's3', 'b2', 'local'],
  },
  {
    path: 'storage.b2Endpoint',
    category: 'storage',
    englishLabel: 'Backblaze B2 endpoint',
    englishHelp: 'S3-compatible endpoint URL for Backblaze B2 (e.g. `https://s3.us-west-001.backblazeb2.com`).',
    keywords: ['storage', 'b2', 'backblaze', 'endpoint'],
    relatedSettings: ['storage.b2Bucket', 'storage.b2KeyId', 'storage.b2ApplicationKey'],
  },
  {
    path: 'storage.b2Bucket',
    category: 'storage',
    englishLabel: 'Backblaze B2 bucket name',
    englishHelp: 'Bucket name where Rynora writes artefacts when `storage.provider` is `b2`.',
    keywords: ['storage', 'b2', 'backblaze', 'bucket'],
  },
  {
    path: 'storage.b2KeyId',
    category: 'storage',
    englishLabel: 'Backblaze B2 key id',
    englishHelp: 'Application key id (S3-compatible access-key analogue) for Backblaze B2.',
    keywords: ['storage', 'b2', 'backblaze', 'key', 'id'],
  },
  {
    path: 'storage.b2ApplicationKey',
    category: 'storage',
    englishLabel: 'Backblaze B2 application key',
    englishHelp: 'Application key (S3-compatible secret analogue) for Backblaze B2.',
    keywords: ['storage', 'b2', 'backblaze', 'application', 'key', 'secret'],
  },
  // ---- visualAi ----
  {
    path: 'visualAi.enabled',
    category: 'noCode',
    englishLabel: 'Enable visual-AI region diff',
    englishHelp:
      'Master switch for the SSIM-based visual-AI region diff. When off, visual baselines fall back to byte-equality.',
    keywords: ['visual', 'ai', 'enabled', 'ssim', 'killswitch'],
    relatedSettings: ['visualAi.ssimThreshold', 'visualAi.windowSize'],
  },
  {
    path: 'visualAi.ssimThreshold',
    category: 'noCode',
    englishLabel: 'Visual-AI SSIM threshold',
    englishHelp:
      'Region SSIM (0–1) below which the region counts as different. Default 0.97. Lower numbers tolerate more noise.',
    keywords: ['visual', 'ai', 'ssim', 'threshold'],
  },
  {
    path: 'visualAi.windowSize',
    category: 'noCode',
    englishLabel: 'Visual-AI SSIM window size (px)',
    englishHelp:
      'Sliding-window size for SSIM computation. Larger windows are more forgiving of local noise but lose fine detail.',
    keywords: ['visual', 'ai', 'ssim', 'window', 'size'],
  },
  {
    path: 'visualAi.dynamicMaskingPresets',
    category: 'noCode',
    englishLabel: 'Visual-AI dynamic-masking presets',
    englishHelp:
      'Curated regex patterns that mask dynamic UI before SSIM compare (timestamps, ad slots, marquees). Edit as JSON.',
    keywords: ['visual', 'ai', 'masking', 'dynamic', 'presets'],
  },
  // pending-applitools-visual-ai v1.2 — cross-browser matrix sub-slice.
  {
    path: 'visualAi.matrix.enabled',
    category: 'noCode',
    englishLabel: 'Enable cross-browser visual matrix',
    englishHelp:
      'Master switch for the Ultrafast Grid-style cross-browser visual fan-out. When on, baselines can be compared across N browser cells in one go and the review panel renders a per-cell SSIM grid.',
    keywords: ['visual', 'ai', 'matrix', 'cross-browser', 'ultrafast', 'grid', 'killswitch'],
    relatedSettings: [
      'visualAi.enabled',
      'visualAi.ssimThreshold',
      'visualAi.matrix.defaultCells',
      'visualAi.matrix.maxCells',
    ],
  },
  {
    path: 'visualAi.matrix.defaultCells',
    category: 'noCode',
    englishLabel: 'Visual-matrix default cells',
    englishHelp:
      'Ordered list of browser cells the cross-browser visual matrix offers by default. Each entry carries a friendly label plus an execution capability (engine / browser / OS / optional viewport / device). The first cell is the anchor unless overridden. Edit as JSON.',
    keywords: ['visual', 'ai', 'matrix', 'cells', 'default', 'cross-browser', 'capability'],
  },
  {
    path: 'visualAi.matrix.anchorCellLabel',
    category: 'noCode',
    englishLabel: 'Visual-matrix anchor cell label',
    englishHelp:
      'Optional label of the anchor cell every other cell is diffed against. When unset, the first entry in default cells becomes the anchor. Per-run overrides win at submission time.',
    keywords: ['visual', 'ai', 'matrix', 'anchor', 'reference', 'cell'],
  },
  {
    path: 'visualAi.matrix.maxCells',
    category: 'noCode',
    englishLabel: 'Visual-matrix max cells per run',
    englishHelp:
      'Per-org ceiling on cells in a single visual-matrix run. Default 8 mirrors Applitools Ultrafast Grid free tier; hard cap 20 mirrors the parallel-runs capability limit. Raise carefully — each cell consumes one parallel worker slot.',
    keywords: ['visual', 'ai', 'matrix', 'max', 'cells', 'limit', 'cap'],
  },
];

// ---------------------------------------------------------------------------
// Scenarios — curated.
// ---------------------------------------------------------------------------

export const PLAIN_ENGLISH_SCENARIOS: PlainEnglishScenario[] = [
  {
    id: 'slack-on-failure',
    englishQuestion: 'Get a Slack message when a run fails',
    targetTab: 'integrations',
    highlightFields: ['integrations.slackEnabled', 'notifications.channels.slack.enabled'],
    walkthroughSteps: [
      { selector: '[data-field="integrations.slackEnabled"]', label: 'Turn on the Slack integration master switch.' },
      {
        selector: '[data-field="notifications.channels.slack.enabled"]',
        label: 'Then flip the per-channel toggle on so failures + approvals route through Slack.',
      },
    ],
  },
  {
    id: 'open-jira-on-failure',
    englishQuestion: 'Open a Jira ticket when a run fails',
    targetTab: 'jira',
    highlightFields: ['jira.enabled', 'jira.componentMap', 'jira.autoAddToActiveSprint'],
    walkthroughSteps: [
      { selector: '[data-field="jira.enabled"]', label: 'Turn Jira hook on (requires existing Jira OAuth setup).' },
      { selector: '[data-field="jira.autoAddToActiveSprint"]', label: 'Optional — attach to the current sprint.' },
    ],
  },
  {
    id: 'enable-sso-google',
    englishQuestion: 'Let my team sign in with Google',
    targetTab: 'auth',
    highlightFields: ['auth.googleEnabled', 'auth.googleClientId', 'auth.googleClientSecret'],
  },
  {
    id: 'enable-sso-microsoft',
    englishQuestion: 'Let my team sign in with Microsoft / Entra',
    targetTab: 'auth',
    highlightFields: ['auth.microsoftClientId', 'auth.microsoftClientSecret', 'auth.microsoftTenant'],
  },
  {
    id: 'require-approval-before-prod',
    englishQuestion: 'Require approval before a run promotes to production',
    targetTab: 'approval',
    highlightFields: ['approval.runGate.enabled', 'approval.notifications.email.enabled'],
  },
  {
    id: 'maintenance-mode',
    englishQuestion: 'Put the app into maintenance mode for a deploy',
    targetTab: 'ops',
    highlightFields: ['ops.maintenanceMode', 'ops.maintenanceMessage'],
    walkthroughSteps: [
      { selector: '[data-field="ops.maintenanceMode"]', label: 'Flip this to On — all routes start returning 503.' },
      { selector: '[data-field="ops.maintenanceMessage"]', label: 'Edit the message visitors see on the page.' },
    ],
  },
  {
    id: 'turn-off-runner',
    englishQuestion: 'Pause the test runner without taking the app down',
    targetTab: 'ops',
    highlightFields: ['ops.flags.runnerEnabled'],
  },
  {
    id: 'auto-logout-idle',
    englishQuestion: 'Log out idle users automatically (SOC2)',
    targetTab: 'security',
    highlightFields: ['security.flowBuilderIdleWarnMinutes', 'security.flowBuilderIdleLogoutMinutes'],
  },
  {
    id: 'nightly-dast',
    englishQuestion: 'Schedule a nightly security scan on my production app',
    targetTab: 'security',
    highlightFields: ['security.dast.enabled', 'security.dast.targetUrl', 'security.dast.hourUtc'],
  },
  {
    id: 'change-from-address',
    englishQuestion: 'Change the From address on outbound emails',
    targetTab: 'email',
    highlightFields: ['email.smtpFrom', 'email.provider'],
  },
  {
    id: 'use-resend',
    englishQuestion: 'Switch outbound email to Resend instead of SMTP',
    targetTab: 'email',
    highlightFields: ['email.provider', 'email.resendApiKey', 'email.resendFromDomain'],
  },
  {
    id: 'set-openai-key',
    englishQuestion: 'Use my own OpenAI key for AI features',
    targetTab: 'ai',
    highlightFields: ['ai.openaiApiKey', 'ai.openaiModel'],
  },
  {
    id: 'switch-to-claude',
    englishQuestion: 'Use Anthropic Claude for natural-language step parsing',
    targetTab: 'ai',
    highlightFields: ['ai.anthropicApiKey'],
  },
  {
    id: 'use-groq',
    englishQuestion: 'Use Groq for sub-second AI parsing',
    targetTab: 'ai',
    highlightFields: ['ai.groqApiKey'],
  },
  {
    id: 'extend-run-retention',
    englishQuestion: 'Keep run reports longer (e.g. for compliance)',
    targetTab: 'reporting',
    highlightFields: ['reporting.retention.maxAgeDays', 'storage.retentionDays'],
  },
  {
    id: 'pdf-email-reports',
    englishQuestion: 'Send the run summary email as a PDF instead of HTML',
    targetTab: 'reporting',
    highlightFields: ['reporting.emailFormat'],
  },
  {
    id: 'kill-public-share-links',
    englishQuestion: 'Disable public report share-links (compliance)',
    targetTab: 'reporting',
    highlightFields: ['reporting.publicShareLinksEnabled'],
  },
  {
    id: 'increase-parallelism',
    englishQuestion: 'Run more browsers in parallel',
    targetTab: 'runner',
    highlightFields: ['runner.maxConcurrentSessions'],
  },
  {
    id: 'cloud-browser-on',
    englishQuestion: 'Send my runs to BrowserStack',
    targetTab: 'cloudBrowser',
    highlightFields: ['cloudBrowser.enabled', 'cloudBrowser.provider', 'cloudBrowser.browserstackUsername'],
  },
  {
    id: 'tighter-a11y',
    englishQuestion: 'Use stricter (AAA) contrast checking',
    targetTab: 'accessibility',
    highlightFields: ['accessibility.contrastWcagLevel'],
  },
  {
    id: 'show-tutorial-to-new-users',
    englishQuestion: 'Show the first-flow tutorial to new users',
    targetTab: 'onboarding',
    highlightFields: ['onboarding.firstFlowTutorial.enabled'],
  },
  {
    id: 'cdn-purge',
    englishQuestion: 'Purge the CDN cache after every deploy',
    targetTab: 'cdn',
    highlightFields: ['cdn.cloudflareApiToken', 'cdn.cloudflareZoneId', 'cdn.purgeOnDeploy'],
  },
  {
    id: 'set-stripe',
    englishQuestion: 'Connect Stripe for billing',
    targetTab: 'billing',
    highlightFields: ['billing.stripeSecretKey'],
    walkthroughSteps: [
      { selector: '[data-field="billing.stripeSecretKey"]', label: 'Paste your Stripe secret key here.' },
    ],
  },
];

// ---------------------------------------------------------------------------
// Search helpers — pure, used by UI + tests.
// ---------------------------------------------------------------------------

/**
 * Lightweight fuzzy matcher. Scores a query against a `FieldMeta` by:
 *   • exact path / label match → score 100
 *   • path / label / keyword substring → score 60–80 (longer match wins)
 *   • all query tokens individually present somewhere → score 30–50
 *   • otherwise 0 (filtered out)
 *
 * Deliberately stupid — no Levenshtein — to keep bundle small. Good
 * enough for ≤ 1k registry entries.
 */
export interface SearchHit {
  meta: AdminConfigFieldMeta;
  score: number;
}

export function searchAdminRegistry(
  query: string,
  registry: AdminConfigFieldMeta[] = ADMIN_CONFIG_REGISTRY,
): SearchHit[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const tokens = q.split(/\s+/).filter(Boolean);
  const hits: SearchHit[] = [];
  for (const meta of registry) {
    const haystack = [meta.path, meta.englishLabel, meta.englishHelp, ...meta.keywords]
      .map((s) => s.toLowerCase())
      .join(' \u0001 ');

    let score = 0;
    if (meta.path.toLowerCase() === q || meta.englishLabel.toLowerCase() === q) {
      score = 100;
    } else if (haystack.includes(q)) {
      score = 60 + Math.min(20, q.length); // longer query matched = higher
    } else if (tokens.every((t) => haystack.includes(t))) {
      score = 30 + Math.min(20, tokens.length * 5);
    }
    if (score > 0) hits.push({ meta, score });
  }
  hits.sort((a, b) => b.score - a.score || a.meta.path.localeCompare(b.meta.path));
  return hits;
}

/** Returns the entry for a given dot-path, or undefined. */
export function findFieldMeta(
  path: string,
  registry: AdminConfigFieldMeta[] = ADMIN_CONFIG_REGISTRY,
): AdminConfigFieldMeta | undefined {
  return registry.find((m) => m.path === path);
}

/**
 * v2 convenience — return the hand-authored registry concatenated
 * with auto-derived rows for every uncovered `AdminConfigSchema`
 * leaf. Callers that want "full coverage" semantics (the ⌘K palette,
 * the help side panel, the registry-driven faceted form) should use
 * this instead of `ADMIN_CONFIG_REGISTRY` directly.
 *
 * Memoised — the schema is static at module load, so the walker only
 * runs once per process.
 */
let _mergedCache: AdminConfigFieldMeta[] | null = null;
export function getMergedAdminConfigRegistry(opts?: AutoDeriveOptions): AdminConfigFieldMeta[] {
  if (_mergedCache && !opts) return _mergedCache;
  const leaves = walkSchemaLeaves(AdminConfigSchema);
  const merged = mergeRegistry(ADMIN_CONFIG_REGISTRY, leaves, opts);
  if (!opts) _mergedCache = merged;
  return merged;
}

/** Returns the curated scenarios, optionally filtered by free-text. */
export function searchScenarios(
  query: string,
  scenarios: PlainEnglishScenario[] = PLAIN_ENGLISH_SCENARIOS,
): PlainEnglishScenario[] {
  const q = query.trim().toLowerCase();
  if (!q) return scenarios;
  return scenarios.filter((s) => s.englishQuestion.toLowerCase().includes(q) || s.targetTab.toLowerCase().includes(q));
}
